
/* Rivers Refined - Soft Wall Room Designer - layout geometry.
   Shared by both builds. Pure functions, every length in inches.
   Wall coordinates: x from the left corner (as seen from inside the room), y up from the floor. */
(function (root) {
'use strict';

const EPS = 1e-9;
const TINY_AREA = 0.01; // sq in; anything smaller is a numerical crumb, not a piece

// ---------------------------------------------------------------- lengths

function parseInchPart(t) {
  t = t.trim();
  if (!t) return 0;
  let m;
  if ((m = t.match(/^(\d+(?:\.\d+)?|\.\d+)$/))) return +m[1];
  if ((m = t.match(/^(\d+)[\s-]+(\d+)\/(\d+)$/))) return +m[3] > 0 ? +m[1] + m[2] / m[3] : NaN;
  if ((m = t.match(/^(\d+)\/(\d+)$/))) return +m[2] > 0 ? m[1] / m[2] : NaN;
  return NaN;
}

/** "14' 6\"", "14ft 6in", "14'-6 1/2\"", "174", "174.5", "6 1/2" -> inches. Invalid -> NaN. */
function parseLength(input) {
  if (typeof input === 'number') return isFinite(input) && input >= 0 ? input : NaN;
  if (input == null) return NaN;
  let s = String(input).trim().toLowerCase()
    .replace(/[′’]/g, "'").replace(/[″”“]/g, '"')
    .replace(/''/g, '"')
    .replace(/(?<![a-z])(feet|foot|ft)(?![a-z])\.?/g, "'")
    .replace(/(?<![a-z])(inches|inch|in)(?![a-z])\.?/g, '"');
  if (!s || s[0] === '-') return NaN;
  let feet = 0, rest = s;
  const fi = s.indexOf("'");
  if (fi >= 0) {
    const f = s.slice(0, fi).trim();
    if (!/^(\d+(\.\d+)?|\.\d+)$/.test(f)) return NaN;
    feet = +f;
    rest = s.slice(fi + 1);
  }
  if (rest.indexOf("'") >= 0) return NaN;
  rest = rest.replace(/"/g, ' ').replace(/^[\s,-]+/, '').trim();
  const inch = parseInchPart(rest);
  if (!isFinite(inch)) return NaN;
  return feet * 12 + inch;
}

function gcd(a, b) { return b ? gcd(b, a % b) : a; }

/** 23.5625 -> "23 9/16" (nearest 1/16). */
function fmtFrac(x, den) {
  den = den || 16;
  const neg = x < 0; x = Math.abs(x);
  let whole = Math.floor(x + EPS);
  let n = Math.round((x - whole) * den);
  if (n >= den) { whole += 1; n = 0; }
  let s;
  if (n === 0) s = String(whole);
  else { const g = gcd(n, den); s = (whole ? whole + ' ' : '') + (n / g) + '/' + (den / g); }
  return (neg ? '-' : '') + s;
}
function fmtIn(x) { return fmtFrac(x) + '"'; }
function fmtFtIn(x) {
  const t = Math.round(x * 16) / 16;
  const ft = Math.floor(t / 12 + EPS);
  const inch = t - ft * 12;
  if (ft === 0) return fmtFrac(inch) + '"';
  return ft + "' " + fmtFrac(inch) + '"';
}

// ---------------------------------------------------------------- polygons
// A polygon is an array of [x, y]; closed implicitly. Convex polygons are kept CCW.

function signedArea(p) {
  let a = 0;
  for (let i = 0, n = p.length; i < n; i++) {
    const q = p[i], r = p[(i + 1) % n];
    a += q[0] * r[1] - r[0] * q[1];
  }
  return a / 2;
}
function area(p) { return p && p.length >= 3 ? Math.abs(signedArea(p)) : 0; }
function ensureCCW(p) { return signedArea(p) < 0 ? p.slice().reverse() : p.slice(); }

/** Remove repeated and collinear vertices. */
function clean(p) {
  if (!p || p.length < 3) return [];
  let pts = [];
  for (const v of p) {
    const last = pts[pts.length - 1];
    if (!last || Math.abs(last[0] - v[0]) > 1e-7 || Math.abs(last[1] - v[1]) > 1e-7) pts.push([v[0], v[1]]);
  }
  if (pts.length > 1) {
    const a = pts[0], b = pts[pts.length - 1];
    if (Math.abs(a[0] - b[0]) <= 1e-7 && Math.abs(a[1] - b[1]) <= 1e-7) pts.pop();
  }
  let changed = true;
  while (changed && pts.length >= 3) {
    changed = false;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[(i - 1 + pts.length) % pts.length], b = pts[i], c = pts[(i + 1) % pts.length];
      const cr = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
      const len = Math.hypot(c[0] - a[0], c[1] - a[1]) || 1;
      if (Math.abs(cr) / len < 1e-7) { pts.splice(i, 1); changed = true; break; }
    }
  }
  return pts.length >= 3 ? pts : [];
}

function rectPoly(x0, y0, x1, y1) { return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]; }

function bbox(p) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const v of p) {
    if (v[0] < x0) x0 = v[0]; if (v[0] > x1) x1 = v[0];
    if (v[1] < y0) y0 = v[1]; if (v[1] > y1) y1 = v[1];
  }
  return { x0, y0, x1, y1, w: x1 - x0, h: y1 - y0 };
}

function centroid(p) {
  let cx = 0, cy = 0, a = 0;
  for (let i = 0, n = p.length; i < n; i++) {
    const q = p[i], r = p[(i + 1) % n];
    const f = q[0] * r[1] - r[0] * q[1];
    a += f; cx += (q[0] + r[0]) * f; cy += (q[1] + r[1]) * f;
  }
  if (Math.abs(a) < EPS) { const b = bbox(p); return [(b.x0 + b.x1) / 2, (b.y0 + b.y1) / 2]; }
  return [cx / (3 * a), cy / (3 * a)];
}

/** Keep the part of a convex polygon where a*x + b*y <= c (Sutherland-Hodgman, one plane). */
function clipHalfPlane(poly, a, b, c) {
  const out = [];
  const n = poly.length;
  if (n < 3) return [];
  for (let i = 0; i < n; i++) {
    const P = poly[i], Q = poly[(i + 1) % n];
    const dp = a * P[0] + b * P[1] - c;
    const dq = a * Q[0] + b * Q[1] - c;
    const inP = dp <= 1e-9, inQ = dq <= 1e-9;
    if (inP) out.push(P);
    if (inP !== inQ) {
      const t = dp / (dp - dq);
      out.push([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t]);
    }
  }
  return clean(out);
}

function clipRect(poly, x0, y0, x1, y1) {
  let p = clipHalfPlane(poly, -1, 0, -x0);
  p = clipHalfPlane(p, 1, 0, x1);
  p = clipHalfPlane(p, 0, -1, -y0);
  p = clipHalfPlane(p, 0, 1, y1);
  return p;
}

/** Outward unit normals of a CCW polygon's edges. */
function edgeNormals(p) {
  const out = [];
  for (let i = 0, n = p.length; i < n; i++) {
    const a = p[i], b = p[(i + 1) % n];
    const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1;
    out.push([dy / L, -dx / L]);
  }
  return out;
}

/** Intersection of two convex polygons (CCW). */
function convexIntersection(a, b) {
  let r = ensureCCW(a);
  const bb = ensureCCW(b), nb = edgeNormals(bb);
  for (let i = 0; i < bb.length && r.length >= 3; i++) {
    const n = nb[i], p = bb[i];
    r = clipHalfPlane(r, n[0], n[1], n[0] * p[0] + n[1] * p[1]);
  }
  return r;
}

/**
 * Offset a convex polygon. d < 0 moves every edge inward by |d| (exact: intersection of the
 * shifted half-planes, so edges that vanish are handled). d > 0 moves every edge outward with
 * mitred corners; with opts.squareLimit, a corner whose mitre would reach further than
 * squareLimit*d from the original vertex is squared off at distance d instead.
 */
function offsetConvex(poly, d, opts) {
  opts = opts || {};
  const p = ensureCCW(clean(poly));
  if (p.length < 3) return [];
  if (Math.abs(d) < EPS) return p.map(v => [v[0], v[1]]);
  const ns = edgeNormals(p);
  if (d < 0) {
    let r = p;
    for (let i = 0; i < p.length && r.length >= 3; i++) {
      const n = ns[i], q = p[i];
      r = clipHalfPlane(r, n[0], n[1], n[0] * q[0] + n[1] * q[1] + d);
    }
    return clean(r);
  }
  const n = p.length, out = [];
  for (let i = 0; i < n; i++) {
    const v = p[i];
    const n0 = ns[(i - 1 + n) % n], n1 = ns[i];
    // offset lines: n0 . x = n0 . v + d   and   n1 . x = n1 . v + d
    const c0 = n0[0] * v[0] + n0[1] * v[1] + d, c1 = n1[0] * v[0] + n1[1] * v[1] + d;
    const det = n0[0] * n1[1] - n0[1] * n1[0];
    let m;
    if (Math.abs(det) < 1e-12) m = [v[0] + n1[0] * d, v[1] + n1[1] * d];
    else m = [(c0 * n1[1] - c1 * n0[1]) / det, (n0[0] * c1 - n1[0] * c0) / det];
    const reach = Math.hypot(m[0] - v[0], m[1] - v[1]);
    if (opts.squareLimit && reach > opts.squareLimit * d + 1e-9) {
      let bx = n0[0] + n1[0], by = n0[1] + n1[1];
      const bl = Math.hypot(bx, by) || 1; bx /= bl; by /= bl;
      const cs = bx * v[0] + by * v[1] + d; // square line: b . x = cs
      const ints = [];
      for (const [nn, cc] of [[n0, c0], [n1, c1]]) {
        const dt = nn[0] * by - nn[1] * bx;
        ints.push([(cc * by - cs * nn[1]) / dt, (nn[0] * cs - bx * cc) / dt]);
      }
      out.push(ints[0], ints[1]);
    } else out.push(m);
  }
  return clean(out);
}

/** Minimum width of a convex polygon (smallest distance between parallel supporting lines over edge directions). */
function minWidth(p) {
  p = ensureCCW(p);
  if (p.length < 3) return 0;
  const ns = edgeNormals(p);
  let best = Infinity;
  for (let i = 0; i < p.length; i++) {
    const n = ns[i], c = n[0] * p[i][0] + n[1] * p[i][1];
    let far = 0;
    for (const v of p) far = Math.max(far, c - (n[0] * v[0] + n[1] * v[1]));
    best = Math.min(best, far);
  }
  return best;
}

function pointSegDist(pt, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L2 = dx * dx + dy * dy;
  let t = L2 ? ((pt[0] - a[0]) * dx + (pt[1] - a[1]) * dy) / L2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(pt[0] - (a[0] + t * dx), pt[1] - (a[1] + t * dy));
}
/** Distance between two non-intersecting polygons (0 if they overlap). */
function polyDistance(a, b) {
  if (area(convexIntersection(a, b)) > 1e-9) return 0;
  let d = Infinity;
  for (const [P, Q] of [[a, b], [b, a]]) {
    for (const v of P) for (let i = 0; i < Q.length; i++) d = Math.min(d, pointSegDist(v, Q[i], Q[(i + 1) % Q.length]));
  }
  return d;
}

function isAxisRect(p) {
  const q = clean(p);
  if (q.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const a = q[i], b = q[(i + 1) % 4];
    if (Math.abs(a[0] - b[0]) > 1e-7 && Math.abs(a[1] - b[1]) > 1e-7) return false;
  }
  return true;
}

function pointInConvex(pt, p) {
  p = ensureCCW(p);
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    if ((b[0] - a[0]) * (pt[1] - a[1]) - (b[1] - a[1]) * (pt[0] - a[0]) < -1e-9) return false;
  }
  return true;
}

function rotatePoly(p, quarterTurns) {
  let q = p.map(v => [v[0], v[1]]);
  for (let k = 0; k < ((quarterTurns % 4) + 4) % 4; k++) q = q.map(v => [-v[1], v[0]]);
  return q;
}
function translatePoly(p, dx, dy) { return p.map(v => [v[0] + dx, v[1] + dy]); }

// ---------------------------------------------------------------- general (simple, possibly non-convex) polygons
// The curved and notched patterns make pieces that are simple polygons but not convex. Everything
// below works on those; convex pieces keep using the exact convex routines above.

/** True when every turn goes the same way (collinear and near-collinear turns ignored). */
function isConvex(p) {
  const n = p ? p.length : 0;
  if (n < 3) return false;
  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = p[i], b = p[(i + 1) % n], c = p[(i + 2) % n];
    const ux = b[0] - a[0], uy = b[1] - a[1], vx = c[0] - b[0], vy = c[1] - b[1];
    const cr = ux * vy - uy * vx, s = Math.hypot(ux, uy) * Math.hypot(vx, vy);
    if (!(s > 0) || Math.abs(cr) <= 1e-7 * s) continue;
    const k = cr > 0 ? 1 : -1;
    if (!sign) sign = k; else if (k !== sign) return false;
  }
  return true;
}

function pointInPolygon(pt, p) {
  let inside = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const a = p[i], b = p[j];
    if ((a[1] > pt[1]) !== (b[1] > pt[1]) && pt[0] < (b[0] - a[0]) * (pt[1] - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
}
function pointInAny(pt, p) { return isConvex(p) ? pointInConvex(pt, p) : pointInPolygon(pt, p); }

/** Proper or touching intersection of segments ab and cd: the point, or null. */
function segIntersect(a, b, c, d, strict) {
  const rx = b[0] - a[0], ry = b[1] - a[1], sx = d[0] - c[0], sy = d[1] - c[1];
  const den = rx * sy - ry * sx, qx = c[0] - a[0], qy = c[1] - a[1];
  const lr = Math.hypot(rx, ry), ls = Math.hypot(sx, sy);
  if (Math.abs(den) <= 1e-10 * lr * ls) {
    // parallel: they only meet if collinear and overlapping (never a proper crossing)
    if (strict || !(lr > 0) || Math.abs(qx * ry - qy * rx) > 1e-9 * lr) return null;
    const t0 = (qx * rx + qy * ry) / (lr * lr), t1 = ((d[0] - a[0]) * rx + (d[1] - a[1]) * ry) / (lr * lr);
    if (Math.max(t0, t1) < -1e-9 || Math.min(t0, t1) > 1 + 1e-9) return null;
    const t = Math.min(1, Math.max(0, Math.max(Math.min(t0, t1), 0)));
    return [a[0] + t * rx, a[1] + t * ry];
  }
  const t = (qx * sy - qy * sx) / den, u = (qx * ry - qy * rx) / den;
  const e = strict ? 1e-9 : -1e-9;
  if (t <= e || t >= 1 - e || u <= e || u >= 1 - e) return null;
  return [a[0] + t * rx, a[1] + t * ry];
}

/** No two non-adjacent edges cross. */
function isSimple(p) {
  const n = p.length;
  for (let i = 0; i < n; i++) for (let j = i + 2; j < n; j++) {
    if (i === 0 && j === n - 1) continue;
    if (segIntersect(p[i], p[(i + 1) % n], p[j], p[(j + 1) % n], true)) return false;
  }
  return true;
}

/** Cut the loops out of a self-crossing ring (what an offset leaves at tight concave bends). Keeps the CCW body. */
function untangle(ring) {
  let pts = ring.slice();
  for (let guard = 0; guard < 400; guard++) {
    const n = pts.length;
    if (n < 4) return pts;
    let hit = null;
    for (let i = 0; i < n && !hit; i++) {
      const a = pts[i], b = pts[(i + 1) % n];
      const ax0 = Math.min(a[0], b[0]), ax1 = Math.max(a[0], b[0]), ay0 = Math.min(a[1], b[1]), ay1 = Math.max(a[1], b[1]);
      for (let j = i + 2; j < n; j++) {
        if (i === 0 && j === n - 1) continue;
        const c = pts[j], d = pts[(j + 1) % n];
        if (Math.max(c[0], d[0]) < ax0 || Math.min(c[0], d[0]) > ax1 || Math.max(c[1], d[1]) < ay0 || Math.min(c[1], d[1]) > ay1) continue;
        const X = segIntersect(a, b, c, d, true);
        if (X) { hit = { i, j, X }; break; }
      }
    }
    if (!hit) return pts;
    const A = pts.slice(0, hit.i + 1).concat([hit.X], pts.slice(hit.j + 1));
    const B = [hit.X].concat(pts.slice(hit.i + 1, hit.j + 1));
    pts = signedArea(A) >= signedArea(B) ? A : B;
  }
  return pts;
}

/**
 * Offset a simple polygon (convex or not). d < 0 moves every edge in, d > 0 out, with mitred
 * corners. Convex input goes through offsetConvex (exact). opts.squareLimit squares off a convex
 * corner whose mitre reaches further than squareLimit*d (outward offsets only).
 */
function offsetSimple(poly, d, opts) {
  opts = opts || {};
  const p = ensureCCW(clean(poly));
  if (p.length < 3) return [];
  if (isConvex(p)) return offsetConvex(p, d, opts);
  if (Math.abs(d) < EPS) return p.map(v => [v[0], v[1]]);
  const n = p.length, ns = edgeNormals(p), out = [];
  for (let i = 0; i < n; i++) {
    const v = p[i], n0 = ns[(i - 1 + n) % n], n1 = ns[i];
    const c0 = n0[0] * v[0] + n0[1] * v[1] + d, c1 = n1[0] * v[0] + n1[1] * v[1] + d;
    const det = n0[0] * n1[1] - n0[1] * n1[0];
    let m;
    if (Math.abs(det) < 1e-9) m = [v[0] + n1[0] * d, v[1] + n1[1] * d];
    else m = [(c0 * n1[1] - c1 * n0[1]) / det, (n0[0] * c1 - n1[0] * c0) / det];
    const reach = Math.hypot(m[0] - v[0], m[1] - v[1]);
    if (opts.squareLimit && d > 0 && det > 0 && reach > opts.squareLimit * d + 1e-9) {
      let bx = n0[0] + n1[0], by = n0[1] + n1[1];
      const bl = Math.hypot(bx, by) || 1; bx /= bl; by /= bl;
      const cs = bx * v[0] + by * v[1] + d;
      for (const [nn, cc] of [[n0, c0], [n1, c1]]) {
        const dt = nn[0] * by - nn[1] * bx;
        out.push([(cc * by - cs * nn[1]) / dt, (nn[0] * cs - bx * cc) / dt]);
      }
    } else out.push(m);
  }
  let r = clean(untangle(clean(out)));
  // A mitre can land where an edge too short for the offset has folded back on itself (a hairline
  // spike, or an inlet at a short shoulder). Every true offset point is |d| from the outline, so a
  // point nearer than that (or, for an inset, outside it) is dropped, and the ring re-joined.
  const ad = Math.abs(d);
  for (let pass = 0; pass < 6 && r.length >= 3; pass++) {
    const keep = r.filter(v => {
      if (d < 0 && !pointInPolygon(v, p)) return false;
      let m = Infinity;
      for (let i = 0; i < n && m >= ad - 1e-7; i++) m = Math.min(m, pointSegDist(v, p[i], p[(i + 1) % n]));
      return m >= ad - 1e-7;
    });
    if (keep.length === r.length) break;
    r = clean(untangle(keep));
  }
  return r.length >= 3 && signedArea(r) > 0 ? r : [];
}

/**
 * The part of a simple polygon where a*x + b*y <= c, as a list of polygons (a concave shape can
 * fall apart into several). Convex input gives at most one piece, exactly as clipHalfPlane.
 */
function splitHalfPlane(poly, a, b, c) {
  if (!poly || poly.length < 3) return [];
  if (isConvex(poly)) { const q = clipHalfPlane(poly, a, b, c); return q.length >= 3 ? [q] : []; }
  const p = ensureCCW(poly), n = p.length;
  const s = p.map(v => a * v[0] + b * v[1] - c);
  if (s.every(x => x <= 1e-9)) return [p.map(v => [v[0], v[1]])];
  if (s.every(x => x > 1e-9)) return [];
  const ring = [];
  for (let i = 0; i < n; i++) {
    const inP = s[i] <= 1e-9, inQ = s[(i + 1) % n] <= 1e-9;
    ring.push({ p: p[i], inside: inP });
    if (inP !== inQ) {
      const Q = p[(i + 1) % n], t = s[i] / (s[i] - s[(i + 1) % n]);
      ring.push({ p: [p[i][0] + (Q[0] - p[i][0]) * t, p[i][1] + (Q[1] - p[i][1]) * t], inside: true, cross: inP ? 'out' : 'in' });
    }
  }
  const crossIdx = ring.map((r, i) => r.cross ? i : -1).filter(i => i >= 0);
  crossIdx.sort((i, j) => (-b * ring[i].p[0] + a * ring[i].p[1]) - (-b * ring[j].p[0] + a * ring[j].p[1]));
  const partner = {};
  for (let k = 0; k + 1 < crossIdx.length; k += 2) { partner[crossIdx[k]] = crossIdx[k + 1]; partner[crossIdx[k + 1]] = crossIdx[k]; }
  const seen = new Array(ring.length).fill(false), outPolys = [];
  for (let st = 0; st < ring.length; st++) {
    if (!ring[st].inside || seen[st] || ring[st].cross === 'in') continue;
    const poly2 = [];
    let cur = st, guard = 0;
    do {
      if (seen[cur] && cur !== st) break;
      seen[cur] = true; poly2.push(ring[cur].p);
      if (ring[cur].cross === 'out' && partner[cur] != null) { cur = partner[cur]; seen[cur] = true; poly2.push(ring[cur].p); }
      cur = (cur + 1) % ring.length;
    } while (cur !== st && ++guard < ring.length * 2);
    const q = clean(poly2);
    if (q.length >= 3 && area(q) > 1e-9) outPolys.push(ensureCCW(q));
  }
  return outPolys;
}
function splitAll(list, a, b, c) { return list.flatMap(p => splitHalfPlane(p, a, b, c)); }
/** Clip any simple polygon to a rectangle. Returns a list. */
function clipRectAny(poly, x0, y0, x1, y1) {
  if (isConvex(poly)) { const q = clipRect(poly, x0, y0, x1, y1); return q.length >= 3 ? [q] : []; }
  let l = [poly];
  l = splitAll(l, -1, 0, -x0); l = splitAll(l, 1, 0, x1); l = splitAll(l, 0, -1, -y0); l = splitAll(l, 0, 1, y1);
  return l;
}

function convexHull(pts) {
  const p = pts.map(v => [v[0], v[1]]).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], hi = [];
  for (const v of p) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], v) <= 1e-12) lo.pop(); lo.push(v); }
  for (let i = p.length - 1; i >= 0; i--) { const v = p[i]; while (hi.length >= 2 && cross(hi[hi.length - 2], hi[hi.length - 1], v) <= 1e-12) hi.pop(); hi.push(v); }
  lo.pop(); hi.pop();
  return lo.concat(hi);
}
function diameter(p) {
  let d = 0;
  for (let i = 0; i < p.length; i++) for (let j = i + 1; j < p.length; j++) d = Math.max(d, Math.hypot(p[i][0] - p[j][0], p[i][1] - p[j][1]));
  return d;
}
/**
 * How wide a piece is, for the sliver rule. Convex: the true minimum width. Not convex: the width
 * of its hull, or, for a long piece, its area over its length (catches a thin curved strip whose
 * hull is fat).
 */
function pieceWidth(p) {
  if (isConvex(p)) return minWidth(p);
  const h = convexHull(p), hw = minWidth(h), dm = diameter(h);
  return dm > 2.5 * hw ? Math.min(hw, area(p) / dm) : hw;
}

/** Distance between two simple polygons, convex or not (0 when they overlap or touch). */
function polyDistanceAny(A, B) {
  if (pointInPolygon(A[0], B) || pointInPolygon(B[0], A)) return 0;
  let d = Infinity;
  for (let i = 0; i < A.length; i++) {
    const a = A[i], b = A[(i + 1) % A.length];
    for (let j = 0; j < B.length; j++) {
      const c = B[j], e = B[(j + 1) % B.length];
      if (segIntersect(a, b, c, e, false)) return 0;
      d = Math.min(d, pointSegDist(a, c, e), pointSegDist(c, a, b));
    }
  }
  return d;
}

/** A rectangle sits inside a polygon with at least `margin` clear of its boundary. */
function rectInsideWithMargin(poly, X, margin) {
  const R = rectPoly(X.x0, X.y0, X.x1, X.y1);
  if (!R.every(v => pointInPolygon(v, poly))) return false;
  if (poly.some(v => v[0] > X.x0 && v[0] < X.x1 && v[1] > X.y0 && v[1] < X.y1)) return false;
  return boundaryDistance(R, poly) >= margin;
}
function boundaryDistance(A, B) {
  let d = Infinity;
  for (let i = 0; i < A.length; i++) for (let j = 0; j < B.length; j++) {
    d = Math.min(d, pointSegDist(A[i], B[j], B[(j + 1) % B.length]), pointSegDist(B[j], A[i], A[(i + 1) % A.length]));
  }
  return d;
}

/** Points along a circular arc, a0 to a1 (radians), no step over maxStep radians. */
function arcPoints(cx, cy, rad, a0, a1, maxStep) {
  const n = Math.max(2, Math.ceil(Math.abs(a1 - a0) / (maxStep || 0.07)));
  const out = [];
  for (let k = 0; k <= n; k++) { const a = a0 + (a1 - a0) * k / n; out.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]); }
  return out;
}

// ---------------------------------------------------------------- custom patterns
// A small, closed language for a pattern we do not draw ourselves (the design assistant writes these).
// One repeating cell, w x h inches, divided into shapes. Each shape is a list of corners in cell units
// (0 to 1, y up), and a corner may carry a third number: the bulge of the edge from it to the next
// corner, as a fraction of that edge's length (+ bulges to the left of travel, - to the right; a
// neighbour that shares the curve gives the opposite sign). The shapes must divide the cell exactly,
// so the repeat divides the wall exactly and the usual reveal and cut rules apply unchanged.
const CUSTOM_TILES = [['grid', 'Straight repeat'], ['brick', 'Half over on every row'], ['drop', 'Half up on every column']];
const CUSTOM_LIMITS = { shapes: 16, points: 24, bulge: 0.5, minRadius: 2, minAngleDeg: 15, nameLen: 40 };
function defaultCustom() {
  return { name: 'Scallop bands', tile: 'brick', shapes: [
    [[0, 0], [1, 0], [1, 0.5, 0.18], [0, 0.5]],
    [[0, 0.5, -0.18], [1, 0.5], [1, 1], [0, 1]],
  ] };
}
/** Clean any input into a custom spec of the right shape, or null. Rounds to 1/1000 of a cell. */
function sanitizeCustom(c) {
  if (!c || typeof c !== 'object' || !Array.isArray(c.shapes)) return null;
  const L = CUSTOM_LIMITS, r3 = (x) => Math.round(x * 1000) / 1000;
  if (!c.shapes.length || c.shapes.length > L.shapes) return null;
  const shapes = [];
  for (const sh of c.shapes) {
    if (!Array.isArray(sh) || sh.length < 3 || sh.length > L.points) return null;
    const ring = [];
    for (const v of sh) {
      if (!Array.isArray(v) || v.length < 2 || v.length > 3) return null;
      const x = +v[0], y = +v[1], b = v.length === 3 ? +v[2] : 0;
      if (![x, y, b].every(isFinite) || x < -0.001 || x > 1.001 || y < -0.001 || y > 1.001 || Math.abs(b) > L.bulge) return null;
      const pt = [r3(Math.min(1, Math.max(0, x))), r3(Math.min(1, Math.max(0, y)))];
      if (Math.abs(b) >= 0.001) pt.push(r3(b));
      ring.push(pt);
    }
    shapes.push(ring);
  }
  const tile = CUSTOM_TILES.some(t => t[0] === c.tile) ? c.tile : 'grid';
  const name = String(c.name || 'Custom pattern').replace(/[^\w ,.'&()/-]/g, '').trim().slice(0, L.nameLen) || 'Custom pattern';
  return { name, tile, shapes };
}
/** One shape as a polyline in inches, its arcs sampled the same way from either end, so neighbours share points. */
function customRing(shape, w, h, ox, oy) {
  const out = [], n = shape.length;
  for (let i = 0; i < n; i++) {
    const a = shape[i], b = shape[(i + 1) % n], P = [ox + a[0] * w, oy + a[1] * h], Qp = [ox + b[0] * w, oy + b[1] * h];
    out.push(P);
    const bulge = a[2] || 0, c = Math.hypot(Qp[0] - P[0], Qp[1] - P[1]);
    if (!bulge || c < 1e-6) continue;
    const s = bulge * c, R = (c * c / 4 + s * s) / (2 * Math.abs(s));
    const ux = (Qp[0] - P[0]) / c, uy = (Qp[1] - P[1]) / c, nx = -uy, ny = ux, mx = (P[0] + Qp[0]) / 2, my = (P[1] + Qp[1]) / 2;
    const off = s - Math.sign(s) * R, Cx = mx + nx * off, Cy = my + ny * off;
    const theta = 4 * Math.atan(2 * Math.abs(s) / c), steps = Math.max(2, Math.ceil(theta / 0.1));
    const a0 = Math.atan2(P[1] - Cy, P[0] - Cx), apx = mx + nx * s, apy = my + ny * s;
    const dir = Math.hypot(Cx + R * Math.cos(a0 + theta / 2) - apx, Cy + R * Math.sin(a0 + theta / 2) - apy) <
      Math.hypot(Cx + R * Math.cos(a0 - theta / 2) - apx, Cy + R * Math.sin(a0 - theta / 2) - apy) ? 1 : -1;
    for (let k = 1; k < steps; k++) {
      // measured from whichever end comes first in a fixed order, so the reverse traversal lands on the same points
      const t = k / steps, ang = a0 + dir * theta * t;
      out.push([Cx + R * Math.cos(ang), Cy + R * Math.sin(ang)]);
    }
  }
  return out;
}
/** The arc of one edge: its radius in inches, or Infinity for a straight edge. */
function customRadius(a, b, w, h) {
  const bulge = a[2] || 0; if (!bulge) return Infinity;
  const c = Math.hypot((b[0] - a[0]) * w, (b[1] - a[1]) * h), s = Math.abs(bulge) * c;
  return (c * c / 4 + s * s) / (2 * s);
}
/**
 * Can this pattern be built at this size? Every rule a person would check before cutting:
 * the shapes divide the cell (no gap, no overlap), nothing crosses itself, no piece is narrower
 * than the minimum, no curve is tighter than the fabric will wrap, no point is too sharp.
 * Returns { ok, errors: [plain words], info }.
 */
function validateCustom(spec, opts) {
  opts = opts || {};
  const errors = [], c = sanitizeCustom(spec);
  if (!c) return { ok: false, errors: ['The pattern is not in the expected shape: 1 to ' + CUSTOM_LIMITS.shapes + ' shapes, each 3 to ' + CUSTOM_LIMITS.points + ' corners between 0 and 1, bulges no more than ' + CUSTOM_LIMITS.bulge + '.'], info: {} };
  const w = Math.min(240, Math.max(4, +opts.moduleW || 24)), h = Math.min(240, Math.max(4, +opts.moduleH || 24));
  const r = opts.reveal >= 0 ? +opts.reveal : 0.375, minPiece = opts.minPiece >= 0 ? +opts.minPiece : 3;
  const rings = c.shapes.map(sh => ensureCCW(clean(customRing(sh, w, h, 0, 0))));
  let total = 0;
  rings.forEach((p, k) => {
    if (p.length < 3 || area(p) < 0.5) { errors.push('Shape ' + (k + 1) + ' has no area.'); return; }
    if (!isSimple(p)) errors.push('Shape ' + (k + 1) + ' crosses itself.');
    total += area(p);
  });
  if (errors.length) return { ok: false, errors, info: {} };
  if (Math.abs(total - w * h) > 0.01 * w * h) errors.push(total < w * h ? 'The shapes leave part of the cell uncovered (' + Math.round(100 - 100 * total / (w * h)) + '% open).' : 'The shapes overlap (' + Math.round(100 * total / (w * h) - 100) + '% too much).');
  // every sample point in the cell, clear of the edges, must sit in exactly one shape
  const N = 40, margin = 0.06 * Math.min(w, h) / N + 0.02;
  let gap = 0, over = 0;
  const near = (pt, p) => { for (let i = 0; i < p.length; i++) if (pointSegDist(pt, p[i], p[(i + 1) % p.length]) < margin) return true; return false; };
  for (let a = 0; a < N; a++) for (let b = 0; b < N; b++) {
    const pt = [(a + 0.5) / N * w, (b + 0.5) / N * h];
    if (rings.some(p => near(pt, p))) continue;
    const k = rings.filter(p => pointInPolygon(pt, p)).length;
    if (k === 0) gap++; else if (k > 1) over++;
  }
  if (gap > N * N * 0.004 && !errors.some(e => /uncovered/.test(e))) errors.push('The shapes leave gaps inside the cell.');
  if (over > N * N * 0.004 && !errors.some(e => /overlap/.test(e))) errors.push('Some shapes overlap each other.');
  // buildability, at the size it will be cut
  c.shapes.forEach((sh, k) => {
    for (let i = 0; i < sh.length; i++) {
      const R = customRadius(sh[i], sh[(i + 1) % sh.length], w, h);
      if (R < CUSTOM_LIMITS.minRadius) { errors.push('A curve on shape ' + (k + 1) + ' is ' + fmtIn(R) + ' in radius; fabric will not wrap tighter than ' + fmtIn(CUSTOM_LIMITS.minRadius) + '. Make the pattern larger or the curve gentler.'); break; }
    }
    const p = rings[k], n = p.length;
    for (let i = 0; i < n; i++) {
      const A = p[(i + n - 1) % n], B = p[i], C = p[(i + 1) % n];
      const v1 = [A[0] - B[0], A[1] - B[1]], v2 = [C[0] - B[0], C[1] - B[1]];
      const ang = Math.acos(Math.max(-1, Math.min(1, (v1[0] * v2[0] + v1[1] * v2[1]) / (Math.hypot(...v1) * Math.hypot(...v2) || 1)))) * 180 / Math.PI;
      const convexCorner = (v2[0] * v1[1] - v2[1] * v1[0]) > 0;
      if (convexCorner && ang < CUSTOM_LIMITS.minAngleDeg) { errors.push('Shape ' + (k + 1) + ' has a point of ' + Math.round(ang) + ' degrees; the sharpest we wrap is ' + CUSTOM_LIMITS.minAngleDeg + '.'); break; }
    }
    const inset = isConvex(p) ? offsetConvex(p, -r / 2) : offsetSimple(p, -r / 2);
    const wd = inset.length >= 3 ? pieceWidth(inset) : 0;
    if (wd < minPiece) errors.push('Shape ' + (k + 1) + ' is ' + fmtIn(Math.max(0, wd)) + ' at its narrowest at this size; the smallest piece we build is ' + fmtIn(minPiece) + '.');
  });
  return { ok: errors.length === 0, errors, info: { spec: c, moduleW: w, moduleH: h, shapes: c.shapes.length, curved: c.shapes.some(sh => sh.some(v => v[2])) } };
}

// ---------------------------------------------------------------- designs

const DESIGN_TYPES = [
  ['none', 'No panels'],
  ['grid', 'Panel grid'],
  ['channels', 'Vertical channels'],
  ['bands', 'Horizontal bands'],
  ['diamonds', 'Diamonds'],
  ['chevrons', 'Chevrons'],
  ['bond', 'Running bond'],
  ['ashlar', 'Ashlar stack'],
  ['triangles', 'Triangles'],
  ['tumbling', 'Tumbling blocks'],
  ['lattice', 'Faceted lattice'],
  ['framed', 'Framed bevel'],
  ['facets', 'Faceted columns'],
  ['fishbone', 'Fishbone'],
  ['arrow', 'Arrow herringbone'],
  ['broken', 'Broken diagonal'],
  ['alternate', 'Alternating columns'],
  ['fan', 'Fan lines'],
  ['scurve', 'S-curve channels'],
  ['arch', 'Pointed arch'],
  ['curved', 'Curved columns'],
  ['hexes', 'Hexagon lattice'],
  ['capsule', 'Capsules'],
  ['decofan', 'Deco fan channels'],
  ['diagbands', 'Diagonal bands'],
  ['roundel', 'Round mirror'],
  ['flow', 'Flowing lines'],
  ['facetcols', 'Diagonal facets'],
  ['custom', 'Custom pattern'],
];

// Per-pattern starting values. The five original patterns have none, so they are unchanged.
const TYPE_DEFAULTS = {
  bond: { moduleW: 24, moduleH: 36 },
  ashlar: { moduleW: 22, moduleH: 26, mirror: true },
  triangles: { moduleW: 24, angle: 62 },
  tumbling: { moduleW: 22, moduleH: 25.5 },
  lattice: { moduleW: 20, angle: 55, mirror: true },
  framed: { moduleW: 30, moduleH: 36, count: 22 },
  facets: { moduleW: 13, angle: 55, mirror: true },
  fishbone: { moduleW: 12, moduleH: 10, angle: 35 },
  arrow: { moduleW: 9, angle: 45, count: 4 },
  broken: { moduleW: 24, moduleH: 30, angle: 18 },
  alternate: { moduleW: 16, moduleH: 14, angle: 30 },
  fan: { moduleH: 20, angle: 40, surface: 'quilted', inlay: 'gold', lineScope: 'all' },
  scurve: { moduleW: 14, moduleH: 32, count: 70, inlay: 'gold' },
  arch: { moduleW: 12, count: 175 },
  curved: { moduleW: 14, count: 65 },
  hexes: { moduleW: 18, moduleH: 29 },
  capsule: { moduleW: 12, moduleH: 40, led: 'lines', lineScope: 'all' },
  decofan: { moduleW: 14, moduleH: 12, count: 45, inlay: 'gold' },
  diagbands: { moduleW: 16, angle: 45 },
  roundel: { moduleW: 44, count: 0, mirror: true },
  flow: { moduleW: 16, count: 3, inlay: 'gold' },
  facetcols: { moduleW: 22, angle: 45, inlay: 'gold' },
  custom: { moduleW: 24, moduleH: 18 },
};
// Trim options. Inlay = a metal strip in the reveals; led = a light channel; surface = how the fabric face is sewn.
const INLAY_TYPES = [['none', 'None'], ['gold', 'Gold'], ['black', 'Black'], ['chrome', 'Chrome']];
const LED_TYPES = [['none', 'Off'], ['lines', 'In the reveals'], ['edge', 'Halo behind'], ['both', 'Both']];
const SURFACE_TYPES = [['plain', 'Plain'], ['channel', 'Channel stitch'], ['quilted', 'Quilted diamond']];
const LINE_SCOPES = [['feature', 'Feature lines'], ['all', 'Every reveal']];
/** Patterns whose layout has open spaces that take a mirror insert (or, for the ashlar stack, a mirror band). */
const MIRROR_TYPES = ['ashlar', 'lattice', 'facets', 'roundel'];

function defaultDesign(type) {
  const d = {
    type: type || 'grid',
    mode: 'count',        // grid/channels/bands: 'count' or 'size'
    rows: 3, cols: 4,     // grid by count
    targetW: 24, targetH: 36, // grid by size
    count: 8,             // channels / bands by count
    size: 16,             // channel width / band height by size
    angle: 45,            // diamonds / chevrons, degrees from horizontal
    moduleW: 18, moduleH: 12,
    color: '#2B2B2E',
    accentOn: false, accentColor: '#B49C78', accentMode: 'alternate',
    inlay: 'none', lineScope: 'feature', led: 'none', mirror: false, surface: 'plain', ledTemp: 'warm', ledLevel: 0.7,
  };
  Object.assign(d, TYPE_DEFAULTS[d.type] || {});
  if (d.type === 'custom') d.custom = defaultCustom();
  return d;
}

function countFor(len, target, gap) {
  if (!(target > 0)) return 1;
  return Math.max(1, Math.round((len + gap) / (target + gap)));
}
function evenSplit(a, b, n, gap) {
  const w = (b - a - (n - 1) * gap) / n, out = [];
  for (let i = 0; i < n; i++) out.push([a + i * (w + gap), a + i * (w + gap) + w]);
  return { spans: out, size: w };
}
function mod(a, n) { return ((a % n) + n) % n; }
function middle(i, n) { return n % 2 ? i === (n - 1) / 2 : (i === n / 2 - 1 || i === n / 2); }

/**
 * Cells for one zone. R = usable rectangle {x0,y0,x1,y1}. r = reveal between panels.
 * Rectangular designs fill R exactly (sizes + gaps = R). Angled designs tile the plane
 * centred on R and are clipped to it afterwards.
 */
function designCells(d, R, r) {
  const Uw = R.x1 - R.x0, Uh = R.y1 - R.y0;
  const cells = [], info = {};
  const acc = (cond) => !!(d.accentOn && cond);
  if (d.type === 'grid') {
    const cols = d.mode === 'size' ? countFor(Uw, d.targetW, r) : Math.max(1, Math.round(d.cols) || 1);
    const rows = d.mode === 'size' ? countFor(Uh, d.targetH, r) : Math.max(1, Math.round(d.rows) || 1);
    const X = evenSplit(R.x0, R.x1, cols, r), Y = evenSplit(R.y0, R.y1, rows, r);
    for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
      const [x0, x1] = X.spans[i], [y0, y1] = Y.spans[j];
      cells.push({ poly: rectPoly(x0, y0, x1, y1), i, j, raw: rectPoly(x0 - r / 2, y0 - r / 2, x1 + r / 2, y1 + r / 2),
        accent: acc(d.accentMode === 'center' ? middle(i, cols) : (i + j) % 2 === 1) });
    }
    Object.assign(info, { rows, cols, panelW: X.size, panelH: Y.size, feature: 'all' });
  } else if (d.type === 'channels') {
    const n = d.mode === 'size' ? countFor(Uw, d.size, r) : Math.max(1, Math.round(d.count) || 1);
    const X = evenSplit(R.x0, R.x1, n, r);
    X.spans.forEach(([x0, x1], i) => cells.push({ poly: rectPoly(x0, R.y0, x1, R.y1), i, j: 0, raw: rectPoly(x0 - r / 2, R.y0 - r / 2, x1 + r / 2, R.y1 + r / 2),
      accent: acc(d.accentMode === 'center' ? middle(i, n) : i % 2 === 1) }));
    Object.assign(info, { count: n, panelW: X.size, panelH: Uh, feature: 'all' });
  } else if (d.type === 'bands') {
    const n = d.mode === 'size' ? countFor(Uh, d.size, r) : Math.max(1, Math.round(d.count) || 1);
    const Y = evenSplit(R.y0, R.y1, n, r);
    Y.spans.forEach(([y0, y1], j) => cells.push({ poly: rectPoly(R.x0, y0, R.x1, y1), i: 0, j, raw: rectPoly(R.x0 - r / 2, y0 - r / 2, R.x1 + r / 2, y1 + r / 2),
      accent: acc(d.accentMode === 'center' ? middle(j, n) : j % 2 === 1) }));
    Object.assign(info, { count: n, panelW: Uw, panelH: Y.size, feature: 'all' });
  } else if (d.type === 'diamonds') {
    const ang = Math.min(75, Math.max(15, +d.angle || 45)) * Math.PI / 180;
    const w = Math.max(2, +d.moduleW || 18), h = w * Math.tan(ang);
    const cx = (R.x0 + R.x1) / 2, cy = (R.y0 + R.y1) / 2;
    const J = Math.ceil(Uh / h) + 2, I = Math.ceil(Uw / w) + 2;
    for (let j = -J; j <= J; j++) for (let i = -I; i <= I; i++) {
      const x = cx + i * w + (mod(j, 2) ? w / 2 : 0), y = cy + j * h / 2;
      if (x + w / 2 < R.x0 || x - w / 2 > R.x1 || y + h / 2 < R.y0 || y - h / 2 > R.y1) continue;
      const raw = [[x - w / 2, y], [x, y - h / 2], [x + w / 2, y], [x, y + h / 2]];
      const poly = offsetConvex(raw, -r / 2);
      if (poly.length < 3) continue;
      cells.push({ poly, raw, i, j, accent: acc(d.accentMode === 'center' ? Math.abs(x - cx) < w / 2 - 1e-6 : mod(j, 2) === 1) });
    }
    Object.assign(info, { moduleW: w, moduleH: h, feature: 'all' });
  } else if (d.type === 'chevrons') {
    const ang = Math.min(60, Math.max(10, +d.angle || 30)) * Math.PI / 180;
    const M = Math.max(2, +d.moduleW || 18), P = Math.max(2, +d.moduleH || 12), t = Math.tan(ang);
    const cx = (R.x0 + R.x1) / 2, cy = (R.y0 + R.y1) / 2;
    const tri = (x) => Math.abs(mod(x - cx, 2 * M) - M); // peak (M) on the centre line
    const K = Math.ceil(Uw / 2 / M) + 1;
    const j0 = Math.floor((R.y0 - cy - t * M) / P) - 1, j1 = Math.ceil((R.y1 - cy) / P) + 1;
    for (let k = -K; k < K; k++) {
      const xa = cx + k * M, xb = xa + M;
      if (xb < R.x0 || xa > R.x1) continue;
      const ta = t * tri(xa), tb = t * tri(xb);
      for (let j = j0; j <= j1; j++) {
        const ya = cy + j * P;
        const raw = [[xa, ya + ta], [xb, ya + tb], [xb, ya + tb + P], [xa, ya + ta + P]];
        const b = bbox(raw);
        if (b.y1 < R.y0 || b.y0 > R.y1) continue;
        const poly = offsetConvex(raw, -r / 2);
        if (poly.length < 3) continue;
        cells.push({ poly, raw, i: k, j, accent: acc(d.accentMode === 'center' ? (k === -1 || k === 0) : mod(j, 2) === 1) });
      }
    }
    Object.assign(info, { moduleW: M, moduleH: P, feature: 'vertical' });
  } else if (PATTERNS[d.type]) {
    const Q = { x0: R.x0 - r / 2, y0: R.y0 - r / 2, x1: R.x1 + r / 2, y1: R.y1 + r / 2 };
    const out = PATTERNS[d.type](d, Q, r, acc);
    for (const c of out.cells) {
      const raw = ensureCCW(clean(c.raw));
      if (raw.length < 3) continue;
      // a generator may give the finished shape itself (capsules, roundels); otherwise it is the cell moved in half a reveal
      const poly = c.poly ? ensureCCW(clean(c.poly)) : isConvex(raw) ? offsetConvex(raw, -r / 2) : offsetSimple(raw, -r / 2);
      if (poly.length < 3 || area(poly) <= TINY_AREA) continue;
      cells.push(Object.assign({}, c, { raw, poly }));
    }
    Object.assign(info, out.info);
  }
  return { cells, info };
}

// ---------------------------------------------------------------- the pattern library
// Each generator divides Q (the zone grown by half a reveal on every side) into cells that meet
// exactly, edge to edge. designCells then moves every cell edge in by half a reveal, so two
// neighbours end up exactly one reveal apart and the outer pieces finish flush with the zone.
// Curves are polylines; neighbours share the very same points, so the reveal along a curve is exact.

function num0(v, lo, hi, def) { v = +v; return isFinite(v) ? Math.min(hi, Math.max(lo, v)) : def; }
function frac(x) { return x - Math.floor(x); }
const smoother = (s) => { s = Math.min(1, Math.max(0, s)); return s * s * s * (s * (s * 6 - 15) + 10); };
/** Even split of [a, b] into n parts with no gap. */
function splitEven(a, b, n) { const w = (b - a) / n, out = []; for (let i = 0; i < n; i++) out.push([a + i * w, i === n - 1 ? b : a + (i + 1) * w]); return out; }
/** Split [a, b] into parts proportional to weights. */
function splitWeighted(a, b, weights) {
  const tot = weights.reduce((s, x) => s + x, 0), out = [];
  let x = a;
  weights.forEach((w, i) => { const nx = i === weights.length - 1 ? b : x + (b - a) * w / tot; out.push([x, nx]); x = nx; });
  return out;
}
/** Keep lo <= a*x + b*y <= hi of a convex polygon. */
function band(poly, a, b, lo, hi) { return clipHalfPlane(clipHalfPlane(poly, a, b, hi), -a, -b, -lo); }
/** A cell between two boundary polylines that each run bottom to top (left one, right one). */
function between(left, right) { return right.concat(left.slice().reverse()); }

const PATTERNS = {
  // ref: staggered rectangles, half a block over on every other row (running bond)
  bond(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0;
    const rows = Math.max(1, Math.round(H / num0(d.moduleH, 4, 240, 36))), cols = Math.max(1, Math.round(W / num0(d.moduleW, 4, 240, 24)));
    const bw = W / cols, bh = H / rows, cx = (Q.x0 + Q.x1) / 2, cells = [];
    for (let j = 0; j < rows; j++) {
      const y0 = Q.y0 + j * bh, y1 = j === rows - 1 ? Q.y1 : y0 + bh, off = j % 2 ? bw / 2 : 0;
      for (let k = -1; k <= cols; k++) {
        const a = Math.max(Q.x0, Q.x0 + k * bw - off), b = Math.min(Q.x1, Q.x0 + (k + 1) * bw - off);
        if (b - a < 1e-6) continue;
        cells.push({ raw: rectPoly(a, y0, b, y1), i: k, j, accent: acc(d.accentMode === 'center' ? Math.abs((a + b) / 2 - cx) < bw / 2 : (k + j) % 2 === 1) });
      }
    }
    return { cells, info: { rows, cols, moduleW: bw, moduleH: bh, panelW: bw - r, panelH: bh - r, feature: 'vertical' } };
  },

  // ref: columns of mixed width, each a stack of blocks of mixed height, with an optional mirror band
  ashlar(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, AW = [1, 1.3, 0.85, 1.15, 0.95, 1.25], AH = [1, 0.45, 1.3, 0.6, 0.9, 0.4, 1.15];
    const cw = num0(d.moduleW, 6, 240, 22), bh = num0(d.moduleH, 4, 240, 26);
    const n = Math.max(1, Math.round(W / cw)), cols = splitWeighted(Q.x0, Q.x1, Array.from({ length: n }, (_, i) => AW[i % AW.length]));
    const bandH = Math.min(10, Math.max(6, bh * 0.3)), withBand = !!d.mirror && H > bandH * 5;
    const yb0 = Q.y0 + Math.round(H * 0.36), yb1 = yb0 + bandH;
    const spans = withBand ? [[Q.y0, yb0], [yb1, Q.y1]] : [[Q.y0, Q.y1]];
    const cx = (Q.x0 + Q.x1) / 2, cells = [];
    cols.forEach(([xa, xb], i) => {
      let k = (i * 3) % AH.length, j = 0;
      for (const [s0, s1] of spans) {
        let y = s0;
        while (y < s1 - 1e-9) {
          let h = AH[k % AH.length] * bh; k++;
          if (s1 - (y + h) < 0.42 * bh) h = s1 - y;
          cells.push({ raw: rectPoly(xa, y, xb, y + h), i, j, accent: acc(d.accentMode === 'center' ? (xa < cx && xb > cx) : (k + i) % 2 === 1) });
          y += h; j++;
        }
      }
      if (withBand) cells.push({ raw: rectPoly(xa, yb0, xb, yb1), i, j: -1, kind: 'mirror' });
    });
    return { cells, info: { count: n, moduleW: W / n, moduleH: bh, feature: 'vertical' } };
  },

  // ref: rows split by diagonals into alternating up and down triangles
  triangles(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, t = Math.tan(num0(d.angle, 30, 80, 62) * Math.PI / 180);
    const w0 = num0(d.moduleW, 6, 240, 24);
    const rows = Math.max(1, Math.round(H / (w0 / 2 * t))), rh = H / rows;
    const m = Math.max(2, Math.round(W / (rh / t))), hb = W / m, cells = [];
    for (let j = 0; j < rows; j++) {
      const ya = Q.y0 + j * rh, yb = j === rows - 1 ? Q.y1 : ya + rh;
      const P = (k) => [k === m ? Q.x1 : Q.x0 + k * hb, k % 2 ? yb : ya];
      cells.push({ raw: [[Q.x0, ya], P(1), [Q.x0, yb]], i: -1, j, accent: acc(d.accentMode === 'center' ? false : true) });
      for (let k = 0; k + 2 <= m; k++) cells.push({ raw: [P(k), P(k + 1), P(k + 2)], i: k, j, accent: acc(d.accentMode === 'center' ? Math.abs(k + 1 - m / 2) < 1 : k % 2 === 1) });
      cells.push({ raw: [P(m - 1), P(m), [Q.x1, m % 2 ? ya : yb]], i: m, j, accent: acc(d.accentMode === 'center' ? false : (m - 1) % 2 === 0) });
    }
    return { cells, info: { rows, moduleW: 2 * hb, moduleH: rh, feature: 'all' } };
  },

  // ref: 60/120 degree rhombi, three to a hexagon, reading as stacked cubes
  tumbling(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, w = num0(d.moduleW, 6, 240, 22), hh = num0(d.moduleH, 4, 400, w * 1.1547);
    const cx = (Q.x0 + Q.x1) / 2, cy = (Q.y0 + Q.y1) / 2, I = Math.ceil(W / w / 2) + 2, J = Math.ceil(H / (0.75 * hh) / 2) + 2, cells = [];
    for (let j = -J; j <= J; j++) for (let i = -I; i <= I; i++) {
      const x = cx + i * w + (mod(j, 2) ? w / 2 : 0), y = cy + j * 0.75 * hh;
      const C = [x, y], T = [x, y + hh / 2], UR = [x + w / 2, y + hh / 4], LR = [x + w / 2, y - hh / 4], B = [x, y - hh / 2], LL = [x - w / 2, y - hh / 4], UL = [x - w / 2, y + hh / 4];
      [[C, UR, T, UL], [C, B, LR, UR], [C, UL, LL, B]].forEach((f, k) => {
        const p = clipRect(ensureCCW(f), Q.x0, Q.y0, Q.x1, Q.y1);
        if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i, j, face: k, accent: acc(d.accentMode === 'center' ? (k === 0 && Math.abs(x - cx) < w) : k === 1) });
      });
    }
    return { cells, info: { moduleW: w, moduleH: hh, feature: 'all' } };
  },

  // ref: diamonds faceted into four triangles, with open diamonds between the columns (mirror)
  lattice(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, w = num0(d.moduleW, 6, 240, 20), h = w * Math.tan(num0(d.angle, 25, 75, 55) * Math.PI / 180);
    const cx = (Q.x0 + Q.x1) / 2, cy = (Q.y0 + Q.y1) / 2, I = Math.ceil(W / w / 2) + 2, J = Math.ceil(H / h) + 2, cells = [];
    for (let j = -J; j <= J; j++) for (let i = -I; i <= I; i++) {
      const x = cx + i * w + (mod(j, 2) ? w / 2 : 0), y = cy + j * h / 2;
      if (x + w / 2 < Q.x0 || x - w / 2 > Q.x1 || y + h / 2 < Q.y0 || y - h / 2 > Q.y1) continue;
      const L = [x - w / 2, y], B = [x, y - h / 2], Rr = [x + w / 2, y], T = [x, y + h / 2], C = [x, y];
      const open = mod(j, 2) === 1 && mod(i, 2) === 0;
      const parts = open ? [[[L, B, Rr, T], -1]] : [[[L, B, C], 0], [[B, Rr, C], 1], [[Rr, T, C], 2], [[T, L, C], 3]];
      for (const [f, k] of parts) {
        const p = clipRect(ensureCCW(f), Q.x0, Q.y0, Q.x1, Q.y1);
        if (p.length < 3 || area(p) <= TINY_AREA) continue;
        cells.push(open ? { raw: p, i, j, kind: 'mirror' } : { raw: p, i, j, accent: acc(d.accentMode === 'center' ? Math.abs(x - cx) < w / 2 : k % 2 === 1) });
      }
    }
    return { cells, info: { moduleW: w, moduleH: h, feature: 'all' } };
  },

  // ref: rectangular modules, each a centre panel inside a mitred frame of four trapezoids
  framed(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0;
    const cols = Math.max(1, Math.round(W / num0(d.moduleW, 8, 240, 30))), rows = Math.max(1, Math.round(H / num0(d.moduleH, 8, 240, 36)));
    const X = splitEven(Q.x0, Q.x1, cols), Y = splitEven(Q.y0, Q.y1, rows), cells = [];
    const f = num0(d.count, 8, 40, 22) / 100 * Math.min(W / cols, H / rows);
    X.forEach(([a, b], i) => Y.forEach(([c, e], j) => {
      const A = acc(d.accentMode === 'center' ? false : true), M = acc(d.accentMode === 'center' ? middle(i, cols) : false);
      cells.push({ raw: rectPoly(a + f, c + f, b - f, e - f), i, j, accent: M });
      cells.push({ raw: [[a, c], [b, c], [b - f, c + f], [a + f, c + f]], i, j, accent: A });
      cells.push({ raw: [[b, c], [b, e], [b - f, e - f], [b - f, c + f]], i, j, accent: A });
      cells.push({ raw: [[b, e], [a, e], [a + f, e - f], [b - f, e - f]], i, j, accent: A });
      cells.push({ raw: [[a, e], [a, c], [a + f, c + f], [a + f, e - f]], i, j, accent: A });
    }));
    return { cells, info: { rows, cols, moduleW: W / cols, moduleH: H / rows, frame: f, feature: 'all' } };
  },

  // ref: vertical columns broken by pointed (hexagonal) inserts at staggered heights; the pieces take a V notch
  facets(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, t = Math.tan(num0(d.angle, 30, 75, 55) * Math.PI / 180);
    const n = Math.max(1, Math.round(W / (num0(d.moduleW, 6, 240, 13) * 0.86)));
    const cols = splitWeighted(Q.x0, Q.x1, Array.from({ length: n }, (_, i) => i % 2 ? 0.72 : 1)), cells = [];
    cols.forEach(([xa, xb], i) => {
      const cw = xb - xa, xm = (xa + xb) / 2, p = cw / 2 * t, s0 = Math.max(cw * 0.9, 6);
      const ins = 2 * s0 + 2 * p, P = ins * 2.4, clear = p + 10;
      const centres = [];
      for (let y = Q.y0 + frac(0.27 + i * 0.618) * P; y < Q.y1; y += P) if (y - ins / 2 > Q.y0 + clear && y + ins / 2 < Q.y1 - clear) centres.push(y);
      let bottom = [[xa, Q.y0], [xb, Q.y0]], j = 0;
      const pieceAcc = acc(d.accentMode === 'center' ? (xa < (Q.x0 + Q.x1) / 2 && xb > (Q.x0 + Q.x1) / 2) : i % 2 === 1);
      for (const yc of centres) {
        const lo = yc - s0, hi = yc + s0;
        const top = [[xb, lo], [xm, lo - p], [xa, lo]];
        cells.push({ raw: bottom.concat(top), i, j: j++, accent: pieceAcc });
        cells.push({ raw: [[xa, lo], [xm, lo - p], [xb, lo], [xb, hi], [xm, hi + p], [xa, hi]], i, j: j++, kind: 'mirror' });
        bottom = [[xa, hi], [xm, hi + p], [xb, hi]];
      }
      cells.push({ raw: bottom.concat([[xb, Q.y1], [xa, Q.y1]]), i, j, accent: pieceAcc });
    });
    return { cells, info: { count: n, moduleW: W / n, feature: 'all' } };
  },

  // ref: columns of slanted strips meeting at a centre spine (fishbone / wheat)
  fishbone(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, ang = num0(d.angle, 10, 75, 35) * Math.PI / 180, t = Math.tan(ang);
    const n = Math.max(1, Math.round(W / (2 * num0(d.moduleW, 3, 240, 12)))), cw = W / n, half = cw / 2;
    const P = Math.max(num0(d.moduleH, 2, 240, 10), 5 / Math.cos(ang));
    const cells = [], k0 = Math.floor(-t * half / P) - 1, k1 = Math.ceil(H / P) + 1;
    for (let c = 0; c < n; c++) {
      const xa = Q.x0 + c * cw, xm = xa + half, xb = c === n - 1 ? Q.x1 : xa + cw;
      for (let k = k0; k <= k1; k++) {
        const y = Q.y0 + k * P;
        for (const [raw, side] of [[[[xa, y], [xm, y + t * half], [xm, y + t * half + P], [xa, y + P]], 0], [[[xm, y + t * half], [xb, y], [xb, y + P], [xm, y + t * half + P]], 1]]) {
          const p = clipRect(raw, Q.x0, Q.y0, Q.x1, Q.y1);
          if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i: 2 * c + side, j: k, accent: acc(d.accentMode === 'center' ? middle(c, n) : mod(k, 2) === 1) });
        }
      }
    }
    return { cells, info: { count: n, moduleW: half, moduleH: P, feature: 'vertical' } };
  },

  // ref: four quadrants of diagonal stripes, mirrored, meeting in an X at the centre
  arrow(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, ang = num0(d.angle, 20, 70, 45) * Math.PI / 180, t = Math.tan(ang);
    const cols = Math.max(2, 2 * Math.round(num0(d.count, 2, 16, 4) / 2)), cx = (Q.x0 + Q.x1) / 2, cy = (Q.y0 + Q.y1) / 2;
    const Pv = Math.max(num0(d.moduleW, 3, 240, 9), 4) / Math.cos(ang), X = splitEven(Q.x0, Q.x1, cols), cells = [];
    for (const sy of [-1, 1]) for (const sx of [-1, 1]) {
      const ya = sy > 0 ? cy : Q.y0, yb = sy > 0 ? Q.y1 : cy;
      // f = sy*(y - cy) - t*sx*(x - cx) = A x + B y + C
      const A = -t * sx, B = sy, C = -sy * cy + t * sx * cx;
      X.forEach(([xa, xb], ci) => {
        if ((sx < 0 && xb > cx + 1e-9) || (sx > 0 && xa < cx - 1e-9)) return;
        const base = rectPoly(xa, ya, xb, yb);
        const fs = base.map(v => A * v[0] + B * v[1] + C), k0 = Math.floor(Math.min(...fs) / Pv) - 1, k1 = Math.ceil(Math.max(...fs) / Pv);
        for (let k = k0; k <= k1; k++) {
          const p = band(base, A, B, k * Pv - C, (k + 1) * Pv - C);
          if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i: ci, j: k, accent: acc(d.accentMode === 'center' ? (k === 0 || k === -1) && false : mod(k, 2) === 1) });
        }
      });
    }
    return { cells, info: { count: cols, moduleW: Pv * Math.cos(ang), feature: 'cross', cx, cy } };
  },

  // ref: columns of mixed width, each broken by slanted cuts at staggered heights
  broken(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, t = Math.tan(num0(d.angle, 5, 45, 18) * Math.PI / 180);
    const wide = num0(d.moduleW, 6, 240, 24), n = Math.max(1, Math.round(W / (wide * 0.775)));
    const cols = splitWeighted(Q.x0, Q.x1, Array.from({ length: n }, (_, i) => i % 2 ? 0.55 : 1)), cells = [];
    cols.forEach(([xa, xb], i) => {
      const xm = (xa + xb) / 2, s = (i % 2 ? 1 : -1) * t, rise = t * (xb - xa) / 2;
      const P = Math.max(num0(d.moduleH, 6, 240, 30), 2 * rise + 10), cuts = [];
      for (let y = Q.y0 + frac(0.21 + i * 0.382) * P + P * 0.35; y < Q.y1; y += P) if (y - rise > Q.y0 + 8 && y + rise < Q.y1 - 8) cuts.push(y);
      const base = rectPoly(xa, Q.y0, xb, Q.y1);
      // below the cut line y = yc + s (x - xm):  -s x + y <= yc - s xm
      let lowC = null;
      cuts.concat([null]).forEach((yc, j) => {
        let p = base;
        if (lowC !== null) p = clipHalfPlane(p, s, -1, -(lowC - s * xm));
        if (yc !== null) p = clipHalfPlane(p, -s, 1, yc - s * xm);
        if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i, j, accent: acc(d.accentMode === 'center' ? middle(i, n) : i % 2 === 1) });
        lowC = yc;
      });
    });
    return { cells, info: { count: n, moduleW: W / n, feature: 'nonaxis' } };
  },

  // ref: columns that alternate between a chevron column and a plain one
  alternate(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, ang = num0(d.angle, 10, 70, 30) * Math.PI / 180, t = Math.tan(ang);
    const n = Math.max(1, Math.round(W / num0(d.moduleW, 6, 240, 16))), X = splitEven(Q.x0, Q.x1, n);
    const P = Math.max(num0(d.moduleH, 2, 240, 14), 5 / Math.cos(ang)), cells = [];
    X.forEach(([xa, xb], i) => {
      if (i % 2) { cells.push({ raw: rectPoly(xa, Q.y0, xb, Q.y1), i, j: 0, accent: acc(d.accentMode === 'center' ? middle(i, n) : true) }); return; }
      const xm = (xa + xb) / 2, half = (xb - xa) / 2, k0 = -1, k1 = Math.ceil((H + t * half) / P) + 1;
      for (let k = k0; k <= k1; k++) {
        const y = Q.y0 + k * P;
        for (const raw of [[[xa, y], [xm, y - t * half], [xm, y - t * half + P], [xa, y + P]], [[xm, y - t * half], [xb, y], [xb, y + P], [xm, y - t * half + P]]]) {
          const p = clipRect(raw, Q.x0, Q.y0, Q.x1, Q.y1);
          if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i, j: k, accent: acc(d.accentMode === 'center' ? false : false) });
        }
      }
    });
    return { cells, info: { count: n, moduleW: W / n, moduleH: P, feature: 'vertical' } };
  },

  // ref: a centre spine with bent lines rising from it, flat out to the sides, under a top band
  fan(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, t = Math.tan(num0(d.angle, 15, 65, 40) * Math.PI / 180);
    const P = num0(d.moduleH, 6, 240, 20), cx = (Q.x0 + Q.x1) / 2, half = W / 2, Wd = half * 0.62;
    const topH = H > 60 ? Math.min(30, Math.max(10, H * 0.17)) : 0, Yb = Q.y1 - topH, cells = [];
    const k0 = Math.floor(-t * Wd / P) - 1, k1 = Math.ceil((Yb - Q.y0) / P) + 1;
    for (const sx of [-1, 1]) {
      const line = (k) => { const y = Q.y0 + k * P; return [[cx, y], [cx + sx * Wd, y + t * Wd], [cx + sx * half, y + t * Wd]]; };
      const xa = sx < 0 ? Q.x0 : cx, xb = sx < 0 ? cx : Q.x1;
      for (let k = k0; k <= k1; k++) {
        const raw = ensureCCW(line(k).concat(line(k + 1).reverse()));
        for (const p of clipRectAny(raw, xa, Q.y0, xb, Yb)) if (area(p) > TINY_AREA) cells.push({ raw: p, i: sx, j: k, accent: acc(d.accentMode === 'center' ? false : mod(k, 2) === 1) });
      }
    }
    if (topH) {
      [[Q.x0, cx - Wd], [cx - Wd, cx + Wd], [cx + Wd, Q.x1]].forEach(([a, b], i) => cells.push({ raw: rectPoly(a, Yb, b, Q.y1), i, j: 999, accent: acc(d.accentMode === 'center' ? i === 1 : false) }));
    }
    return { cells, info: { moduleW: Wd, moduleH: P, feature: 'all' } };
  },

  // ref: vertical channels whose dividing lines swing sideways in a smooth S
  scurve(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, n = Math.max(2, Math.round(W / num0(d.moduleW, 6, 240, 14))), cw = W / n, cx = (Q.x0 + Q.x1) / 2;
    const amp = Math.min(cw - 4, num0(d.count, 20, 80, 70) / 100 * cw);
    const L = Math.min(H * 0.7, Math.max(12, amp * 2.4, num0(d.moduleH, 6, 240, 32)));
    const bnd = [];
    for (let i = 0; i <= n; i++) {
      const x = i === n ? Q.x1 : Q.x0 + i * cw;
      const jog = i > 0 && i < n && i % 2 === 1 && !(n % 2 === 0 && i === n / 2) && amp > 0.5;
      if (!jog) { bnd.push({ pts: [[x, Q.y0], [x, Q.y1]], jog: false }); continue; }
      const dir = x < cx ? -1 : 1, level = Math.round(Math.abs(x - cx) / cw) % 4 < 2 ? 0.58 : 0.42;
      const yc = Q.y0 + H * level, y0 = yc - L / 2, N = 28, pts = [[x, Q.y0]];
      for (let k = 0; k <= N; k++) { const y = y0 + L * k / N; pts.push([x + dir * amp * smoother(k / N), y]); }
      pts.push([x + dir * amp, Q.y1]);
      bnd.push({ pts, jog: true });
    }
    const cells = [];
    for (let i = 0; i < n; i++) cells.push({ raw: between(bnd[i].pts, bnd[i + 1].pts), i, j: 0, curved: bnd[i].jog || bnd[i + 1].jog, accent: acc(d.accentMode === 'center' ? middle(i, n) : i % 2 === 1) });
    return { cells, info: { count: n, moduleW: cw, panelW: cw - r, feature: 'nonaxis' } };
  },

  // ref: tall channels split at staggered heights by a pointed (lancet) arch
  arch(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, n = Math.max(1, Math.round(W / num0(d.moduleW, 6, 240, 12))), X = splitEven(Q.x0, Q.x1, n), cells = [];
    X.forEach(([xa, xb], i) => {
      // the arch springs from a short shoulder at each side, so neither piece ends in a hairline cusp
      const sh = Math.max(1.2, 0.12 * (xb - xa)), a = (xb - xa) / 2 - sh, xm = (xa + xb) / 2, h = Math.max(a, num0(d.count, 50, 240, 175) / 100 * (xb - xa)); // a lancet: about 1.75 times the channel width
      let ys = Q.y0 + H * (i % 2 ? 0.46 : 0.58);
      ys = Math.min(ys, Q.y1 - h - 10);
      const A = acc(d.accentMode === 'center' ? middle(i, n) : i % 2 === 1);
      if (ys < Q.y0 + 12) { cells.push({ raw: rectPoly(xa, Q.y0, xb, Q.y1), i, j: 0, accent: A }); return; }
      const rho = (a * a + h * h) / (2 * a), th = Math.atan2(h, a - rho), x0 = xa + sh;
      const left = arcPoints(x0 + rho, ys, rho, Math.PI, th, 0.06);       // springing (x0, ys) up to the apex
      left[left.length - 1] = [xm, ys + h];
      const right = left.slice(0, -1).reverse().map(v => [xa + xb - v[0], v[1]]); // apex down to the right springing
      const arc = [[xa, ys]].concat(left, right, [[xb, ys]]);
      cells.push({ raw: [[xa, Q.y0], [xb, Q.y0]].concat(arc.slice().reverse()), i, j: 0, curved: true, accent: A });
      cells.push({ raw: arc.concat([[xb, Q.y1], [xa, Q.y1]]), i, j: 1, curved: true, accent: A });
    });
    return { cells, info: { count: n, moduleW: W / n, feature: 'nonaxis' } };
  },

  // ref: tall stretched hexagons in offset rows (a lens lattice)
  hexes(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, w = num0(d.moduleW, 6, 240, 18), hh = Math.max(w * 0.6, num0(d.moduleH, 4, 400, w * 1.6));
    const cx = (Q.x0 + Q.x1) / 2, cy = (Q.y0 + Q.y1) / 2, I = Math.ceil(W / w / 2) + 2, J = Math.ceil(H / (0.75 * hh) / 2) + 2, cells = [];
    for (let j = -J; j <= J; j++) for (let i = -I; i <= I; i++) {
      const x = cx + i * w + (mod(j, 2) ? w / 2 : 0), y = cy + j * 0.75 * hh;
      const hex = [[x, y - hh / 2], [x + w / 2, y - hh / 4], [x + w / 2, y + hh / 4], [x, y + hh / 2], [x - w / 2, y + hh / 4], [x - w / 2, y - hh / 4]];
      const p = clipRect(hex, Q.x0, Q.y0, Q.x1, Q.y1);
      if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i, j, accent: acc(d.accentMode === 'center' ? Math.abs(x - cx) < w * 1.5 : mod(i + j, 2) === 1) });
    }
    return { cells, info: { moduleW: w, moduleH: hh, feature: 'all' } };
  },

  // ref: tall capsules with fully rounded ends, alternate columns offset by half (curved parts)
  capsule(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, n = Math.max(1, Math.round(W / num0(d.moduleW, 6, 240, 12))), X = splitEven(Q.x0, Q.x1, n);
    const P = Math.max(num0(d.moduleH, 12, 240, 40), (W / n) * 2.2), cells = [];
    X.forEach(([xa, xb], i) => {
      const off = i % 2 ? P / 2 : 0, a = (xb - xa - r) / 2, xm = (xa + xb) / 2;
      for (let y = Q.y0 - off; y < Q.y1; y += P) {
        const ya = Math.max(Q.y0, y), yb = Math.min(Q.y1, y + P);
        if (yb - ya < 1) continue;
        // the stadium: straight sides, half-circle ends, half a reveal in from the slot
        const s0 = y + r / 2 + a, s1 = y + P - r / 2 - a, pts = [];
        for (const v of arcPoints(xm, s0, a, Math.PI, 2 * Math.PI, 0.12)) pts.push(v);
        for (const v of arcPoints(xm, s1, a, 0, Math.PI, 0.12)) pts.push(v);
        const shape = clipRect(pts, Q.x0 + r / 2, Q.y0 + r / 2, Q.x1 - r / 2, Q.y1 - r / 2);
        if (shape.length >= 3 && area(shape) > TINY_AREA) cells.push({ raw: rectPoly(xa, ya, xb, yb), poly: shape, i, j: Math.round(y), curved: true, accent: acc(d.accentMode === 'center' ? middle(i, n) : i % 2 === 1) });
      }
    });
    return { cells, info: { count: n, moduleW: W / n, moduleH: P, feature: 'vertical' } };
  },

  // ref: art-deco channels, each a stack of upward arcs (leaf curves), with metal strips between the channels
  decofan(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, n = Math.max(1, Math.round(W / num0(d.moduleW, 6, 240, 14))), X = splitEven(Q.x0, Q.x1, n);
    const P = Math.max(6, num0(d.moduleH, 4, 240, 12)), riseK = num0(d.count, 10, 90, 45) / 100, cells = [];
    X.forEach(([xa, xb], i) => {
      const cw = xb - xa, rise = Math.min(P * 0.9, cw * riseK);
      const arc = (y0) => {
        if (rise < 0.2) return [[xa, y0], [xb, y0]];
        const a = cw / 2, rho = (a * a + rise * rise) / (2 * rise), cyy = y0 + rise - rho, th = Math.asin(Math.min(1, a / rho));
        return arcPoints((xa + xb) / 2, cyy, rho, Math.PI / 2 + th, Math.PI / 2 - th, 0.05).map(v => [v[0], v[1]]);
      };
      const ks = Math.ceil(H / P) + 1;
      for (let k = -1; k < ks; k++) {
        const lo = arc(Q.y0 + k * P), hi = arc(Q.y0 + (k + 1) * P);
        lo[0][0] = xa; lo[lo.length - 1][0] = xb; hi[0][0] = xa; hi[hi.length - 1][0] = xb;
        const raw = ensureCCW(lo.concat(hi.slice().reverse()));
        for (const p of clipRectAny(raw, xa, Q.y0, xb, Q.y1)) if (area(p) > TINY_AREA) cells.push({ raw: p, i, j: k, curved: true, accent: acc(d.accentMode === 'center' ? middle(i, n) : mod(k, 2) === 1) });
      }
    });
    return { cells, info: { count: n, moduleW: W / n, moduleH: P, feature: 'vertical' } };
  },

  // ref: wide parallel diagonal bands across the whole field
  diagbands(d, Q, r, acc) {
    const ang = num0(d.angle, 15, 75, 45) * Math.PI / 180, P = Math.max(4, num0(d.moduleW, 4, 240, 16)) / Math.sin(ang);
    const t = 1 / Math.tan(ang), base = rectPoly(Q.x0, Q.y0, Q.x1, Q.y1), cells = [];
    // bands of x - t*y between k P and (k+1) P
    const f = base.map(v => v[0] - t * v[1]), k0 = Math.floor(Math.min(...f) / P) - 1, k1 = Math.ceil(Math.max(...f) / P);
    for (let k = k0; k <= k1; k++) {
      const p = band(base, 1, -t, k * P, (k + 1) * P);
      if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i: k, j: 0, accent: acc(d.accentMode === 'center' ? k === 0 : mod(k, 2) === 1) });
    }
    return { cells, info: { moduleW: P * Math.sin(ang), feature: 'all' } };
  },

  // ref: a round (or arched) mirror set into an upholstered field; the panels round it follow the curve
  roundel(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, arch = (+d.count || 0) >= 1, cx = (Q.x0 + Q.x1) / 2;
    const D = Math.min(num0(d.moduleW, 18, 240, 44), W - 8, arch ? H * 0.6 : H - 8), m = Math.max(4, D * 0.12);
    const ch = arch ? D * 1.4 : D, cy = Q.y0 + (H - ch) / 2 + (arch ? 0 : D / 2);
    // the square round the mirror, split into two (arch) or four (circle) pieces each with a curved edge
    const sx0 = cx - D / 2 - m, sx1 = cx + D / 2 + m, sy0 = arch ? cy - m : cy - D / 2 - m, sy1 = arch ? cy + ch + m : cy + D / 2 + m, cells = [];
    const R0 = D / 2, cyc = arch ? cy + ch - R0 : cy;
    // one set of arc points, shared by the mirror and the pieces round it, so the reveal along the curve is exact
    const quarterArc = (sxs, sys) => {
      const a0 = Math.atan2(0, sxs); let a1 = Math.atan2(sys, 0);
      while (a1 - a0 > Math.PI) a1 -= 2 * Math.PI;
      while (a0 - a1 > Math.PI) a1 += 2 * Math.PI;
      const pts = arcPoints(cx, cyc, R0, a0, a1, 0.06);
      pts[0] = [cx + sxs * R0, cyc]; pts[pts.length - 1] = [cx, cyc + sys * R0];
      return pts;                                          // from the horizontal axis round to the vertical one
    };
    const q = { '1,1': quarterArc(1, 1), '-1,1': quarterArc(-1, 1), '-1,-1': quarterArc(-1, -1), '1,-1': quarterArc(1, -1) };
    let outline;
    if (!arch) outline = q['1,1'].concat(q['-1,1'].slice().reverse().slice(1), q['-1,-1'].slice(1), q['1,-1'].slice().reverse().slice(1, -1));
    else outline = [[cx - R0, cy], [cx + R0, cy]].concat(q['1,1'], q['-1,1'].slice().reverse().slice(1));
    const inset = (isConvex(outline) ? offsetConvex : offsetSimple)(outline, -r / 2);
    cells.push({ raw: ensureCCW(clean(outline)), poly: inset, i: 0, j: 0, kind: 'mirror', curved: true });
    if (!arch) {
      for (const [sxs, sys] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        // a corner of the square from the axis lines out, with the quarter circle cut away
        const xb = cx + sxs * (D / 2 + m), yb = cyc + sys * (D / 2 + m), arc = q[sxs + ',' + sys];
        const poly = [[xb, cyc], [xb, yb], [cx, yb]].concat(arc.slice().reverse());
        cells.push({ raw: ensureCCW(clean(poly)), i: sxs, j: sys, curved: true, accent: acc(true) });
      }
    } else {
      for (const sxs of [-1, 1]) {
        const xb = cx + sxs * (D / 2 + m), arc = q[sxs + ',1'];
        const poly = [[cx + sxs * R0, sy0], [xb, sy0], [xb, sy1], [cx, sy1]].concat(arc.slice().reverse().slice(1), [[cx + sxs * R0, cy]]);
        cells.push({ raw: ensureCCW(clean(poly)), i: sxs, j: 0, curved: true, accent: acc(true) });
      }
      cells.push({ raw: rectPoly(cx - R0, sy0, cx + R0, cy), i: 0, j: -1, accent: acc(true) });
    }
    // the rest of the field: plain panels round the square, no wider than a sheet
    const fill = (xa, xb, ya, yb) => {
      if (xb - xa < 1 || yb - ya < 1) return;
      const cols = Math.max(1, Math.ceil((xb - xa) / 46)), rows = Math.max(1, Math.ceil((yb - ya) / 92));
      splitEven(xa, xb, cols).forEach(([a2, b2], i) => splitEven(ya, yb, rows).forEach(([c2, e2], j) => cells.push({ raw: rectPoly(a2, c2, b2, e2), i: 10 + i, j: 10 + j, accent: acc(false) })));
    };
    fill(Q.x0, sx0, Q.y0, Q.y1); fill(sx1, Q.x1, Q.y0, Q.y1); fill(sx0, sx1, Q.y0, sy0); fill(sx0, sx1, sy1, Q.y1);
    return { cells, info: { moduleW: D, feature: 'nonaxis' } };
  },

  // ref: a plain field divided by two to four long flowing lines (curved cuts, gold in the lines)
  flow(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, k = Math.round(num0(d.count, 1, 6, 3)), N = 40, bnd = [];
    const amp = Math.min(num0(d.moduleW, 2, 120, 16), W / (k + 1) * 0.38);  // lines never close on each other
    bnd.push([[Q.x0, Q.y0], [Q.x0, Q.y1]]);
    for (let i = 1; i <= k; i++) {
      const x = Q.x0 + W * i / (k + 1), ph = i * 1.7, pts = [];
      for (let q = 0; q <= N; q++) { const s2 = q / N; pts.push([x + amp * Math.sin(Math.PI * (s2 * 1.3 + ph * 0.3)) * Math.sin(Math.PI * s2), Q.y0 + H * s2]); }
      bnd.push(pts);
    }
    bnd.push([[Q.x1, Q.y0], [Q.x1, Q.y1]]);
    const cells = [];
    for (let i = 0; i <= k; i++) cells.push({ raw: between(bnd[i], bnd[i + 1]), i, j: 0, curved: true, accent: acc(i % 2 === 1) });
    return { cells, info: { count: k + 1, moduleW: W / (k + 1), feature: 'nonaxis' } };
  },

  // ref: columns split by 45-degree diagonals, alternating direction column to column, so the cuts zigzag across
  facetcols(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, n = Math.max(1, Math.round(W / num0(d.moduleW, 6, 240, 22))), X = splitEven(Q.x0, Q.x1, n);
    const t = Math.tan(num0(d.angle, 25, 65, 45) * Math.PI / 180), cw = W / n, P = Math.max(8, num0(d.moduleH, 6, 240, 0) || cw * t), cells = [];
    X.forEach(([xa, xb], i) => {
      const s = i % 2 ? -t : t, x0 = i % 2 ? xb : xa;            // lines y = Q.y0 + k P + s (x - x0): they meet at the column joints
      const base = rectPoly(xa, Q.y0, xb, Q.y1);
      const f = base.map(v => v[1] - s * (v[0] - x0) - Q.y0), k0 = Math.floor(Math.min(...f) / P) - 1, k1 = Math.ceil(Math.max(...f) / P);
      for (let k = k0; k <= k1; k++) {
        const p = band(base, -s, 1, k * P + Q.y0 - s * x0, (k + 1) * P + Q.y0 - s * x0);
        if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i, j: k, accent: acc(d.accentMode === 'center' ? middle(i, n) : mod(k + i, 2) === 1) });
      }
    });
    // the zigzag: every other diagonal (the lines with an even index); 'every reveal' puts metal on all of them
    const featureTest = (a, b) => {
      if (Math.abs(b[0] - a[0]) < 1e-6 || Math.abs(b[1] - a[1]) < 1e-6) return false;
      const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], i = Math.min(n - 1, Math.max(0, Math.floor((m[0] - Q.x0) / cw)));
      const s = i % 2 ? -t : t, x0 = i % 2 ? Q.x0 + (i + 1) * cw : Q.x0 + i * cw;
      return mod(Math.round((m[1] - s * (m[0] - x0) - Q.y0) / P), 2) === 0;
    };
    return { cells, info: { count: n, moduleW: cw, moduleH: P, feature: 'zigzag', featureTest } };
  },

  // ref (simplified): channels that bow outward toward the top, more at the sides, like a fan of columns
  curved(d, Q, r, acc) {
    const W = Q.x1 - Q.x0, H = Q.y1 - Q.y0, n = Math.max(2, Math.round(W / num0(d.moduleW, 6, 240, 14))), cw = W / n, cx = (Q.x0 + Q.x1) / 2;
    const bow = num0(d.count, 10, 80, 65) / 100, N = 30, bnd = [];
    for (let i = 0; i <= n; i++) {
      const x = i === n ? Q.x1 : Q.x0 + i * cw;
      if (i === 0 || i === n) { bnd.push([[x, Q.y0], [x, Q.y1]]); continue; }
      const rel = (x - cx) / (W / 2), pts = [];
      for (let k = 0; k <= N; k++) { const s = k / N; pts.push([x + bow * cw * rel * s * s, Q.y0 + H * s]); }
      bnd.push(pts);
    }
    const cells = [];
    for (let i = 0; i < n; i++) cells.push({ raw: between(bnd[i], bnd[i + 1]), i, j: 0, curved: true, accent: acc(d.accentMode === 'center' ? middle(i, n) : i % 2 === 1) });
    return { cells, info: { count: n, moduleW: cw, panelW: cw - r, feature: 'nonaxis' } };
  },
  // a pattern written in the custom language: the cell repeated across the zone, centred on it
  custom(d, Q, r, acc) {
    const c = sanitizeCustom(d.custom) || defaultCustom();
    const w = num0(d.moduleW, 4, 240, 24), h = num0(d.moduleH, 4, 240, 18), cx = (Q.x0 + Q.x1) / 2, cy = (Q.y0 + Q.y1) / 2;
    const I = Math.ceil((Q.x1 - Q.x0) / w / 2) + 2, J = Math.ceil((Q.y1 - Q.y0) / h / 2) + 2, cells = [];
    const curvedShape = c.shapes.map(sh => sh.some(v => v[2]));
    for (let j = -J; j <= J; j++) for (let i = -I; i <= I; i++) {
      const ox = cx - w / 2 + i * w + (c.tile === 'brick' && mod(j, 2) ? w / 2 : 0);
      const oy = cy - h / 2 + j * h + (c.tile === 'drop' && mod(i, 2) ? h / 2 : 0);
      if (ox > Q.x1 || ox + w < Q.x0 || oy > Q.y1 || oy + h < Q.y0) continue;
      c.shapes.forEach((sh, k) => {
        const ring = ensureCCW(clean(customRing(sh, w, h, ox, oy)));
        if (ring.length < 3) return;
        for (const p of clipRectAny(ring, Q.x0, Q.y0, Q.x1, Q.y1)) {
          if (p.length >= 3 && area(p) > TINY_AREA) cells.push({ raw: p, i: i * 32 + k, j, curved: curvedShape[k], accent: acc(d.accentMode === 'center' ? (i === 0 || i === -1) : k % 2 === 1) });
        }
      });
    }
    return { cells, info: { moduleW: w, moduleH: h, custom: c.name, feature: 'all' } };
  },
};

// ---------------------------------------------------------------- wall layout

const OPENING_TYPES = [
  ['door', 'Door'], ['window', 'Window'], ['screen', 'TV / screen niche'], ['outlet', 'Outlet or switch'], ['glass', 'Glass door'],
];
const BOX_NAMES = { sconce: 'sconce box', riser: 'stepped floor' };
function openingName(o) {
  if (BOX_NAMES[o.type]) return BOX_NAMES[o.type];
  const t = OPENING_TYPES.find(x => x[0] === o.type);
  return (t ? t[1] : 'Opening').replace(/^[A-Z][a-z]/, m => m.toLowerCase()); // keeps 'TV'
}

function splitAround(piece, X, r) {
  const poly = piece.poly;
  if (area(convexIntersection(poly, rectPoly(X.x0, X.y0, X.x1, X.y1))) <= 1e-6) return [piece];
  if (!isConvex(poly)) return splitAroundAny(piece, X, r);
  const L = clipHalfPlane(poly, 1, 0, X.x0);
  const Rt = clipHalfPlane(poly, -1, 0, -X.x1);
  let mx0 = X.x0, mx1 = X.x1;
  if (area(L) > TINY_AREA) mx0 = X.x0 + r;
  if (area(Rt) > TINY_AREA) mx1 = X.x1 - r;
  let mid = clipHalfPlane(poly, -1, 0, -mx0);
  mid = clipHalfPlane(mid, 1, 0, mx1);
  const B = clipHalfPlane(mid, 0, 1, X.y0);
  const T = clipHalfPlane(mid, 0, -1, -X.y1);
  return [L, Rt, B, T].filter(p => area(p) > TINY_AREA).map(p => {
    const o = { poly: p, holes: piece.holes.filter(h => pointInConvex(centroid(h), p)),
      accent: piece.accent, zone: piece.zone, splitBy: X.label };
    if (piece.kind) o.kind = piece.kind;
    if (piece.curved) o.curved = true;
    return o;
  });
}

/** splitAround for a piece that is not convex: the same four regions, each possibly in several parts. */
function splitAroundAny(piece, X, r) {
  const poly = piece.poly, A = (l) => l.reduce((s, p) => s + area(p), 0);
  const L = splitHalfPlane(poly, 1, 0, X.x0);
  const Rt = splitHalfPlane(poly, -1, 0, -X.x1);
  let mx0 = X.x0, mx1 = X.x1;
  if (A(L) > TINY_AREA) mx0 = X.x0 + r;
  if (A(Rt) > TINY_AREA) mx1 = X.x1 - r;
  const mid = splitAll(splitHalfPlane(poly, -1, 0, -mx0), 1, 0, mx1);
  const B = splitAll(mid, 0, 1, X.y0);
  const T = splitAll(mid, 0, -1, -X.y1);
  return L.concat(Rt, B, T).filter(p => area(p) > TINY_AREA).map(p => ({
    poly: p, holes: piece.holes.filter(h => pointInPolygon(centroid(h), p)),
    accent: piece.accent, zone: piece.zone, splitBy: X.label, kind: piece.kind, curved: piece.curved,
  }));
}

function rectContainsWithMargin(poly, X, margin) {
  if (!isConvex(poly)) return rectInsideWithMargin(poly, X, margin);
  const inner = offsetConvex(poly, -margin);
  if (inner.length < 3) return false;
  return rectPoly(X.x0, X.y0, X.x1, X.y1).every(v => pointInConvex(v, inner));
}

const NUM_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
function countWord(n) { return n < NUM_WORDS.length ? NUM_WORDS[n] : String(n); }

/**
 * Lay out one wall.
 * wall = { openings:[{type,x,y,w,h}], wainscot:bool, split, zoneGap, zones:[design, design] }
 * opts = { reveal, edge:{ceiling,floor,corner,opening}, minPiece }
 * Returns { zones:[{rect, info}], pieces:[{poly, holes, accent, zone, color}], openings, warnings:[{level,text}] }
 */
// ---------------------------------------------------------------- wall composition: a feature field with sections beside it
const SECTION_TYPES = [['plain', 'Plain upholstered'], ['fluted', 'Fluted upholstery'], ['mirror', 'Mirror'], ['slats', 'Wood slats'], ['marble', 'Marble or stone look'], ['paint', 'Painted wall'], ['pattern', 'A second pattern']];
// The centre can be the main pattern, or any section type (a tall mirror behind a bed, a slat wall).
const CENTER_TYPES = [['pattern', 'The pattern']].concat(SECTION_TYPES.filter(t => t[0] !== 'pattern'));
const WOODS = [['oak', 'Oak'], ['walnut', 'Walnut']];
const SCONCE_STYLES = [['cylinder', 'Cylinder'], ['globe', 'Globe'], ['bar', 'Linear bar'], ['vbar', 'Tall bar']];
const METALS = [['brass', 'Brass'], ['black', 'Black'], ['chrome', 'Chrome']];
const SCONCE_PLACES = [['flank', 'Flanking the feature'], ['even', 'Evenly spaced'], ['custom', 'Custom']];
const COVE_TYPES = [['none', 'None'], ['top', 'At the ceiling'], ['bottom', 'At the floor'], ['both', 'Both']];
const LED_TEMPS = [['warm', 'Warm'], ['neutral', 'Neutral'], ['cool', 'Cool']];
const SCONCE_BOX = 4.5; // the junction box behind a sconce, square, in inches

function defaultComp() {
  return { width: 72, offset: 0, height: 0, bottomH: 0, link: true, left: 'plain', right: 'plain', top: 'plain', bottom: 'plain',
    slatW: 1.75, slatGap: 0.75, wood: 'oak', flute: 4.5, center: 'pattern' };
}
function defaultSconces() { return { n: 2, style: 'cylinder', finish: 'brass', place: 'flank', y: 60, xs: [] }; }

/**
 * Split the panel field U into the feature field and the sections around it. Flanks run the full
 * height; bands above and below run the feature's width. Joints between them are one reveal.
 * A section narrower than the smallest piece is not made: the feature runs on to the edge instead.
 */
function composeRects(comp, U, r, minPiece) {
  const Uw = U.x1 - U.x0, small = Math.max(3, minPiece);
  const fw = Math.min(Uw, Math.max(12, +comp.width || Uw));
  let fx0 = (U.x0 + U.x1) / 2 + (+comp.offset || 0) - fw / 2;
  fx0 = Math.max(U.x0, Math.min(U.x1 - fw, fx0));
  let fx1 = fx0 + fw;
  if (fx0 - U.x0 - r < small) fx0 = U.x0;
  if (U.x1 - fx1 - r < small) fx1 = U.x1;
  let fy0 = U.y0, fy1 = U.y1;
  const out = [], side = (k) => comp.link && k === 'right' ? comp.left : comp[k];
  const bh = +comp.bottomH || 0;
  if (bh >= small && bh + r + small < U.y1 - U.y0) { out.push({ side: 'bottom', type: side('bottom'), x0: fx0, y0: U.y0, x1: fx1, y1: U.y0 + bh }); fy0 = U.y0 + bh + r; }
  if (+comp.height > 0) {
    fy1 = Math.min(U.y1, fy0 + +comp.height);
    if (U.y1 - fy1 - r < small) fy1 = U.y1;
    else out.push({ side: 'top', type: side('top'), x0: fx0, y0: fy1 + r, x1: fx1, y1: U.y1 });
  }
  if (fx0 > U.x0) out.push({ side: 'left', type: side('left'), x0: U.x0, y0: U.y0, x1: fx0 - r, y1: U.y1 });
  if (fx1 < U.x1) out.push({ side: 'right', type: side('right'), x0: fx1 + r, y0: U.y0, x1: U.x1, y1: U.y1 });
  return { feature: { x0: fx0, y0: fy0, x1: fx1, y1: fy1 }, sections: out };
}
/** A section as a design the generators already know (upholstered, fluted, mirror); null for the rest. */
function sectionDesign(type, R, r, main, comp) {
  const base = { color: main.color, family: main.family, accentColor: main.accentColor, accentFamily: main.accentFamily, accentOn: false };
  const cols = Math.max(1, Math.ceil((R.x1 - R.x0 + r) / (46 + r))), rows = Math.max(1, Math.ceil((R.y1 - R.y0 + r) / (92 + r)));
  if (type === 'plain') return Object.assign(defaultDesign('grid'), base, { mode: 'count', cols, rows });
  // mirror comes in larger sheets than board: up to 72 x 120
  if (type === 'mirror') return Object.assign(defaultDesign('grid'), base, { mode: 'count', mirror: true, allMirror: true,
    cols: Math.max(1, Math.ceil((R.x1 - R.x0 + r) / (72 + r))), rows: Math.max(1, Math.ceil((R.y1 - R.y0 + r) / (120 + r))) });
  if (type === 'fluted') return Object.assign(defaultDesign('channels'), base, { mode: 'size', size: Math.min(12, Math.max(2.5, +comp.flute || 4.5)) });
  return null;
}
const SECTION_NAMES = { left: 'left side', right: 'right side', top: 'band above', bottom: 'band below', center: 'centre' };

/** Where the sconces go. Their junction boxes are cut out of the panels like outlets. */
function sconcePositions(sc, W, H, U, F) {
  if (!sc || !(sc.n > 0)) return [];
  const n = Math.min(8, Math.max(1, Math.round(sc.n))), y = Math.min(H - 8, Math.max(8, +sc.y || 60));
  const clampX = (x) => Math.min(U.x1 - 4, Math.max(U.x0 + 4, x));
  let xs;
  if (sc.place === 'custom') xs = Array.from({ length: n }, (_, i) => isFinite(+(sc.xs || [])[i]) ? +sc.xs[i] : U.x0 + (U.x1 - U.x0) * (i + 1) / (n + 1));
  else if (sc.place === 'flank' && n >= 2) {
    const fullWidth = !F || (F.x0 - U.x0 < 1 && U.x1 - F.x1 < 1);
    const half = fullWidth ? Math.min((U.x1 - U.x0) * 0.3, 42) : (F.x1 - F.x0) / 2 + 9;
    const cx = F ? (F.x0 + F.x1) / 2 : W / 2;
    xs = [cx - half, cx + half];
    for (let i = 2; i < n; i++) xs.push(U.x0 + (U.x1 - U.x0) * (i - 1) / (n - 1));
  } else xs = Array.from({ length: n }, (_, i) => U.x0 + (U.x1 - U.x0) * (i + 1) / (n + 1));
  return xs.map(x => ({ x: clampX(x), y }));
}
function isBoxOpening(t) { return t === 'outlet' || t === 'sconce'; }

// ---------------------------------------------------------------- headboards: a limited-height upholstered piece behind the bed
const BED_SIZES = [['twin', 'Twin', 38, 75], ['full', 'Full', 54, 75], ['queen', 'Queen', 60, 80], ['king', 'King', 76, 80], ['calking', 'Cal King', 72, 84]];
const WING_TYPES = [['none', 'None'], ['nightstands', 'To the nightstands'], ['width', 'A set width'], ['full', 'Full wall']];
function defaultHeadboard() { return { bed: 'queen', wings: 'nightstands', wingW: 24, height: 60, trim: 'gold', base: true, above: 'paint' }; }
function bedSize(key) { const b = BED_SIZES.find(x => x[0] === key) || BED_SIZES[2]; return { key: b[0], name: b[1], w: b[2], l: b[3] }; }
/** The headboard's sections: a centre a little wider than the mattress, and wings either side. */
function headboardRects(hb, U, r) {
  const bed = bedSize(hb.bed), cw = bed.w + 4, cx = (U.x0 + U.x1) / 2, top = Math.min(U.y1, Math.max(U.y0 + 12, +hb.height || 60));
  let c0 = Math.max(U.x0, cx - cw / 2), c1 = Math.min(U.x1, cx + cw / 2);
  const out = [{ side: 'centre', x0: c0, x1: c1 }];
  let ww = hb.wings === 'nightstands' ? 24 : hb.wings === 'width' ? Math.max(4, +hb.wingW || 24) : hb.wings === 'full' ? Infinity : 0;
  if (ww > 0) {
    const l0 = Math.max(U.x0, c0 - r - ww), r1 = Math.min(U.x1, c1 + r + ww);
    if (c0 - r - l0 >= 3) out.unshift({ side: 'left', x0: l0, x1: c0 - r });
    if (r1 - (c1 + r) >= 3) out.push({ side: 'right', x0: c1 + r, x1: r1 });
  }
  return out.map(o => Object.assign(o, { y0: U.y0, y1: top }));
}

// ---------------------------------------------------------------- pillars and tiered floors
const PILLAR_FINISHES = [['marble', 'Marble or stone look'], ['wood', 'Wood'], ['upholstered', 'Upholstered'], ['mirror', 'Mirror']];
function defaultPillars() { return { n: 3, width: 14, depth: 3, finish: 'marble', sconce: true, graze: false }; }
/** Pillar rectangles on a wall: evenly spaced, floor to ceiling. They break the wall into bays. */
function pillarRects(pl, W, H) {
  if (!pl || !(pl.n > 0)) return [];
  const n = Math.min(12, Math.max(1, Math.round(pl.n))), w = Math.min(48, Math.max(4, +pl.width || 14));
  const out = [];
  for (let i = 0; i < n; i++) { const cx = W * (i + 1) / (n + 1); out.push({ x0: cx - w / 2, x1: cx + w / 2, y0: 0, y1: H, depth: Math.min(12, Math.max(1, +pl.depth || 3)) }); }
  return out;
}
function defaultLevels() { return { n: 2, rise: 12, depth: 72, start: 120, seats: true, lights: true }; }
/** The tiers of a stepped floor, front (screen wall, z = 0) to back: [{ z0, z1, h }]. The last tier runs to the back wall. */
function tierSpans(lv, L) {
  if (!lv || !(lv.n > 0)) return [];
  const n = Math.min(4, Math.max(1, Math.round(lv.n))), rise = Math.min(30, Math.max(4, +lv.rise || 12)), dep = Math.max(24, +lv.depth || 72), st = Math.max(0, +lv.start || 0);
  const out = [];
  for (let i = 0; i < n; i++) { const z0 = st + i * dep; if (z0 >= L - 12) break; out.push({ z0, z1: i === n - 1 ? L : Math.min(L, z0 + dep), h: (i + 1) * rise }); }
  return out;
}
/** Where the stepped floor meets a wall, in that wall's own x: [{ x0, x1, h }]. */
function tierRunsOnWall(lv, key, W, L) {
  const t = tierSpans(lv, L);
  if (!t.length) return [];
  if (key === 'left') return t.map(s => ({ x0: L - s.z1, x1: L - s.z0, h: s.h }));
  if (key === 'right') return t.map(s => ({ x0: s.z0, x1: s.z1, h: s.h }));
  if (key === 'back') { const last = t[t.length - 1]; return last.z1 >= L - 1e-6 ? [{ x0: 0, x1: W, h: last.h }] : []; }
  return [];
}

/**
 * Lay out one wall (or the ceiling).
 * wall = { openings:[{type,x,y,w,h}], wainscot:bool, split, zoneGap, zones:[design, design],
 *          slope?, comp? (a feature field with sections), sconces?, cove? }
 * opts = { reveal, edge:{ceiling,floor,corner,opening}, minPiece }
 * Returns { zones:[{rect, info, design, section?, lines, edgeLines}], pieces, inserts, slats, areas, sconces, cove, openings, warnings }
 */
function computeWallLayout(wall, W, H, opts) {
  const r = Math.max(0, +opts.reveal || 0), e = opts.edge, minPiece = Math.max(0, +opts.minPiece || 0);
  const warnings = [];
  const warn = (text) => warnings.push({ level: 'warn', text });
  const note = (text) => warnings.push({ level: 'note', text });

  // A stair: the bottom and/or top of the panel field can slope. Each slope is a line given by its
  // height at the wall's left and right ends; the field keeps the floor (or ceiling) edge gap from it.
  const slopes = sloped(wall.slope, W, e);
  let yLo = e.floor, yHi = H - e.ceiling;
  for (const sl of slopes) { if (sl.side === 'bottom') yLo = Math.max(yLo, sl.lo); else yHi = Math.min(yHi, sl.hi); }
  const U = { x0: e.corner, y0: yLo, x1: W - e.corner, y1: yHi };

  const zoneRects = [];
  let feature = null, headboard = null;
  if (wall.headboard && U.x1 - U.x0 > 24 && U.y1 - U.y0 > 12) {
    const parts = headboardRects(wall.headboard, U, r);
    headboard = { parts, top: parts[0].y1 };
    parts.forEach(pt => zoneRects.push({ x0: pt.x0, y0: pt.y0, x1: pt.x1, y1: pt.y1, name: pt.side === 'centre' ? 'headboard' : pt.side + ' wing', designIndex: 0 }));
    const above = wall.headboard.above;
    if (above && above !== 'paint' && U.y1 - (headboard.top + r) >= 3) zoneRects.push({ x0: U.x0, y0: headboard.top + r, x1: U.x1, y1: U.y1, name: 'wall above', section: { side: 'top', type: above } });
  } else if (wall.comp && U.x1 - U.x0 > 12 && U.y1 - U.y0 > 12) {
    const c = composeRects(wall.comp, U, r, minPiece);
    feature = c.feature;
    const centre = wall.comp.center && wall.comp.center !== 'pattern' ? wall.comp.center : null;
    zoneRects.push(Object.assign({ name: c.sections.length ? 'feature field' : '' }, c.feature, centre ? { section: { side: 'center', type: centre } } : {}));
    c.sections.forEach(sc => zoneRects.push({ x0: sc.x0, y0: sc.y0, x1: sc.x1, y1: sc.y1, name: SECTION_NAMES[sc.side], section: { side: sc.side, type: sc.type } }));
  } else if (wall.wainscot) {
    const split = +wall.split, zg = Math.max(0, +wall.zoneGap || 0);
    zoneRects.push({ x0: e.corner, y0: yLo, x1: W - e.corner, y1: Math.min(split, yHi), name: 'lower zone' });
    zoneRects.push({ x0: e.corner, y0: Math.max(split + zg, yLo), x1: W - e.corner, y1: yHi, name: 'upper zone' });
  } else {
    zoneRects.push({ x0: e.corner, y0: yLo, x1: W - e.corner, y1: yHi, name: '' });
  }

  // pillars: each split the zones into bays; an upholstered or mirror pillar becomes its own zone
  const pillars = headboard ? [] : pillarRects(wall.pillars, W, H);
  if (pillars.length) {
    const cut = [];
    zoneRects.forEach((R, zi) => {
      let x = R.x0;
      const bays = [];
      for (const P of pillars) { if (P.x0 - e.corner > x) bays.push([x, P.x0 - e.corner]); x = Math.max(x, P.x1 + e.corner); }
      if (R.x1 > x) bays.push([x, R.x1]);
      bays.filter(b => b[1] - b[0] > 1).forEach((b, bi) => cut.push(Object.assign({}, R, { x0: b[0], x1: b[1], name: (R.name ? R.name + ', ' : '') + 'bay ' + (bi + 1), designIndex: R.section ? undefined : zi })));
    });
    zoneRects.length = 0; cut.forEach(c => zoneRects.push(c));
    const fin = (wall.pillars.finish === 'upholstered') ? 'plain' : wall.pillars.finish === 'mirror' ? 'mirror' : null;
    if (fin) pillars.forEach((P, i) => zoneRects.push({ x0: P.x0 + 0.25, x1: P.x1 - 0.25, y0: yLo, y1: yHi, name: 'pillar ' + (i + 1), section: { side: 'pillar', type: fin } }));
  }
  const sconces = sconcePositions(wall.sconces, W, H, U, feature);
  const pillarSconces = wall.pillars && wall.pillars.sconce ? pillars.map(P => ({ x: (P.x0 + P.x1) / 2, y: Math.min(H - 12, 66), onPillar: true })) : [];
  const risers = (opts.tiers || []).map(t => ({ type: 'riser', x: t.x0, y: 0, w: t.x1 - t.x0, h: t.h }));
  const openings = [];
  (wall.openings || []).concat(sconces.map(p => ({ type: 'sconce', x: p.x - SCONCE_BOX / 2, y: p.y - SCONCE_BOX / 2, w: SCONCE_BOX, h: SCONCE_BOX })),
    wall.pillars && wall.pillars.finish === 'upholstered' ? pillarSconces.map(p => ({ type: 'sconce', x: p.x - SCONCE_BOX / 2, y: p.y - SCONCE_BOX / 2, w: SCONCE_BOX, h: SCONCE_BOX })) : [], risers).forEach((o, idx) => {
    const name = openingName(o);
    const w = +o.w, h = +o.h, x = +o.x, y = +o.y;
    if (!(w > 0 && h > 0) || !isFinite(x) || !isFinite(y)) { warn('The ' + name + ' (opening ' + (idx + 1) + ') needs a width and a height.'); return; }
    if (x < -1e-6 || y < -1e-6 || x + w > W + 1e-6 || y + h > H + 1e-6) warn('The ' + name + ' (opening ' + (idx + 1) + ') runs past the edge of the wall. Check its size and position.');
    const raw = { x0: Math.max(0, x), y0: Math.max(0, y), x1: Math.min(W, x + w), y1: Math.min(H, y + h) };
    if (raw.x1 - raw.x0 <= 0 || raw.y1 - raw.y0 <= 0) return;
    const g = e.opening;
    if (o.type === 'riser') { openings.push({ type: o.type, idx, raw, label: name, exp: { x0: raw.x0, y0: -1, x1: raw.x1, y1: raw.y1 + e.floor, label: name } }); return; }
    openings.push({ type: o.type, idx, raw, label: name,
      exp: { x0: raw.x0 - g, y0: raw.y0 - g, x1: raw.x1 + g, y1: raw.y1 + g, label: name } });
  });
  for (let a = 0; a < openings.length; a++) for (let b = a + 1; b < openings.length; b++) {
    if (openings[a].type === 'riser' || openings[b].type === 'riser') continue;
    const A = openings[a].raw, B = openings[b].raw;
    if (A.x0 < B.x1 && B.x0 < A.x1 && A.y0 < B.y1 && B.y0 < A.y1)
      warn('Opening ' + (openings[a].idx + 1) + ' and opening ' + (openings[b].idx + 1) + ' overlap each other.');
  }

  const pieces = [], zones = [], areas = [];
  zoneRects.forEach((R, z) => {
    const main = (wall.zones && wall.zones[0]) || defaultDesign('none');
    let d = R.section ? null : ((wall.zones && wall.zones[R.designIndex != null ? R.designIndex : z]) || defaultDesign('none'));
    if (R.section && R.section.type === 'pattern') { d = (wall.zones && wall.zones[1]) || defaultDesign('channels'); R.sidePattern = true; R.section = null; }
    else if (R.section) d = sectionDesign(R.section.type, R, r, main, wall.comp) || Object.assign(defaultDesign('none'), { color: main.color, family: main.family });
    const where = R.name ? 'In the ' + R.name + ', ' : '';
    R.poly = slopes.reduce((poly, sl) => clipHalfPlane(poly, sl.a, sl.b, sl.c), rectPoly(R.x0, R.y0, R.x1, R.y1));
    const zoneOut = { rect: R, info: {}, design: d, lines: [], edgeLines: [] };
    if (R.section) zoneOut.section = R.section;
    zones.push(zoneOut);
    if (R.section && (R.section.type === 'marble' || R.section.type === 'paint')) {
      // by others: an area, not parts
      const poly = R.poly.length >= 3 ? R.poly : [];
      const cut = openings.reduce((s, O) => s + area(convexIntersection(poly, rectPoly(O.raw.x0, O.raw.y0, O.raw.x1, O.raw.y1))), 0);
      if (poly.length) areas.push({ zone: z, side: R.section.side, type: R.section.type, poly, area: Math.max(0, area(poly) - cut) });
      return;
    }
    if (!R.section && d.type === 'none') return;
    if (R.x1 - R.x0 < Math.max(minPiece, 1) || R.y1 - R.y0 < Math.max(minPiece, 1)) {
      warn((R.name ? 'The ' + R.name : 'This wall') + ' has no room for panels. Check the wall size and the zone height.');
      return;
    }
    let cells, info = {};
    if (R.section && R.section.type === 'slats') {
      // vertical wood slats, centred, slat width and gap from the composition
      const sw = Math.min(12, Math.max(0.75, +(wall.comp && wall.comp.slatW) || 1.75)), sg = Math.min(6, Math.max(0.25, +(wall.comp && wall.comp.slatGap) || 0.75));
      const n = Math.max(1, Math.floor((R.x1 - R.x0 + sg) / (sw + sg))), x0 = R.x0 + (R.x1 - R.x0 - (n * sw + (n - 1) * sg)) / 2;
      cells = Array.from({ length: n }, (_, i) => { const a = x0 + i * (sw + sg), poly = rectPoly(a, R.y0, a + sw, R.y1); return { poly, raw: poly, i, j: 0, kind: 'slat' }; });
      info = { count: n, slatW: sw, slatGap: sg, feature: 'all' };
      zoneOut.woodName = ((WOODS.find(x => x[0] === (wall.comp && wall.comp.wood)) || WOODS[0])[1]).toLowerCase();
    } else {
      const dc = designCells(d, R, r);
      cells = dc.cells; info = dc.info;
      if (d.allMirror) cells.forEach(c => { c.kind = 'mirror'; });
    }
    zoneOut.info = info;
    let ps = [];
    for (const c of cells) {
      if (c.kind === 'mirror' && !d.mirror) continue; // an open space: no insert, nothing to cut
      if (isConvex(c.poly)) {
        const p = clipRect(c.poly, R.x0, R.y0, R.x1, R.y1);
        if (area(p) > TINY_AREA) {
          const o = { poly: p, holes: [], accent: c.accent, zone: z, splitBy: null };
          if (c.kind) o.kind = c.kind;
          if (c.curved) o.curved = true;
          ps.push(o);
        }
      } else {
        for (const p of clipRectAny(c.poly, R.x0, R.y0, R.x1, R.y1)) {
          if (area(p) > TINY_AREA) ps.push({ poly: p, holes: [], accent: c.accent, zone: z, splitBy: null, kind: c.kind, curved: !!c.curved });
        }
      }
    }
    // a sloped field: everything past the stair line goes
    for (const sl of slopes) {
      ps = ps.flatMap(p => (isConvex(p.poly) ? [clipHalfPlane(p.poly, sl.a, sl.b, sl.c)] : splitHalfPlane(p.poly, sl.a, sl.b, sl.c))
        .filter(q => q.length >= 3 && area(q) > TINY_AREA).map(q => Object.assign({}, p, { poly: q, slopeCut: true })));
    }
    if (!(R.section && R.section.type === 'slats')) zoneOut.lines = clipSegs(revealLines(cells, R, r, info, openings), slopes);
    const fp = R.poly.length >= 3 ? R.poly : rectPoly(R.x0, R.y0, R.x1, R.y1);
    zoneOut.edgeLines = subtractOpenings(fp.map((v, i) => [v, fp[(i + 1) % fp.length]]), openings);
    // large openings: split the panels around them
    const splitNotes = new Set();
    for (const O of openings) {
      if (isBoxOpening(O.type)) continue;
      let touched = false;
      ps = ps.flatMap(p => {
        const out = splitAround(p, O.exp, r);
        if (out.length !== 1 || out[0] !== p) touched = true;
        return out;
      });
      if (touched) splitNotes.add(O.label);
    }
    // outlets, switches and sconce boxes: always a cut-out in ONE panel, never a split. The panel that holds
    // the box's centre takes the hole; a box across a joint is noted so it can be moved before the build.
    for (const O of openings) {
      if (!isBoxOpening(O.type)) continue;
      const X = O.exp, XR = rectPoly(X.x0, X.y0, X.x1, X.y1);
      const hits = ps.filter(p => area(convexIntersection(p.poly, XR)) > 1e-6);
      if (!hits.length) continue;
      const c = [(O.raw.x0 + O.raw.x1) / 2, (O.raw.y0 + O.raw.y1) / 2];
      const host = hits.find(p => pointInAny(c, p.poly)) || hits.slice().sort((a2, b2) => area(convexIntersection(b2.poly, XR)) - area(convexIntersection(a2.poly, XR)))[0];
      let hole = XR;
      if (!rectContainsWithMargin(host.poly, X, 0.5)) {
        // keep the hole inside its panel: trim it to the panel, half an inch in from the edge
        hole = null;
        for (const [inset, minW] of [[0.5, 0.3], [0.15, 0.2], [0.05, 0.08]]) {
          const inner = (isConvex(host.poly) ? offsetConvex : offsetSimple)(host.poly, -inset);
          const cut = inner.length >= 3 ? clean(convexIntersection(inner, XR)) : [];
          if (cut.length >= 3 && minWidth(convexHull(cut)) > minW && isConvex(cut)) { hole = ensureCCW(cut); break; }
        }
        // a curved panel: shrink the box about its centre until it sits wholly inside
        if (!hole && pointInAny(c, host.poly)) {
          for (let f = 0.9; f > 0.12 && !hole; f *= 0.85) {
            const hw = (X.x1 - X.x0) * f / 2, hh = (X.y1 - X.y0) * f / 2;
            const tr = { x0: c[0] - hw, y0: c[1] - hh, x1: c[0] + hw, y1: c[1] + hh };
            if (rectInsideWithMargin(host.poly, tr, 0.1)) hole = rectPoly(tr.x0, tr.y0, tr.x1, tr.y1);
          }
        }
        warn('The ' + O.label + ' at ' + fmtIn(c[0]) + ' from the left sits on a joint between panels. Move it into a panel, or the plate will overlap the joint.');
      }
      if (hole) {
        const hb = bbox(hole), clash = host.holes.findIndex(h => { const b = bbox(h); return b.x0 < hb.x1 && hb.x0 < b.x1 && b.y0 < hb.y1 && hb.y0 < b.y1; });
        if (clash >= 0) {
          // two boxes side by side become one cut-out, kept inside the panel
          const merged = convexHull(host.holes[clash].concat(hole));
          host.holes[clash] = merged.every(v => pointInAny(v, host.poly)) ? ensureCCW(merged) : host.holes[clash];
        } else host.holes.push(hole);
      }
    }
    splitNotes.forEach(n => note((where ? where + 'panels' : 'Panels') + ' that would cover the ' + n + ' are split around it, so nothing covers the opening.'));
    // slivers
    const dropped = {};
    for (const p of ps) {
      const mw = isConvex(p.poly) ? minWidth(p.poly) : pieceWidth(p.poly);
      if (mw + 1e-6 >= (p.kind === 'slat' ? Math.min(minPiece, 0.75) : minPiece)) { pieces.push(p); continue; }
      const b = bbox(p.poly), loc = [];
      if (b.x0 - R.x0 < 0.01) loc.push('left edge');
      if (R.x1 - b.x1 < 0.01) loc.push('right edge');
      if (R.y1 - b.y1 < 0.01) loc.push(R.name ? 'top of the ' + R.name : 'ceiling');
      if (b.y0 - R.y0 < 0.01) loc.push(R.name ? 'bottom of the ' + R.name : 'floor');
      if (slopes.some(sl => p.poly.some(v => Math.abs(sl.a * v[0] + sl.b * v[1] - sl.c) < 0.01))) loc.push('stair line');
      let key = loc.length ? loc.join(' and ') : '';
      if (p.splitBy) key = key ? key + ', beside the ' + p.splitBy : 'beside the ' + p.splitBy;
      if (!key) key = 'middle of the wall';
      dropped[key] = (dropped[key] || 0) + 1;
    }
    Object.keys(dropped).forEach(k => {
      const n = dropped[k];
      warn(where + countWord(n).toLowerCase().replace(/^./, c => c.toUpperCase()) + ' piece' + (n === 1 ? '' : 's') +
        ' under ' + fmtIn(minPiece) + ' wide at the ' + k + (n === 1 ? ' was' : ' were') +
        ' left out. Adjust the module size or the opening position.');
    });
    if (!ps.length) warn(where + 'the design leaves no panels. Make the module smaller.');
  });

  if (slopes.length && !pieces.length && zoneRects.some(R => R.y1 - R.y0 < 1)) warn('The stair line leaves no room for panels. Check its heights.');
  // mirror inserts and wood slats are kept apart from the fabric panels: they are not upholstered
  const inserts = [], slats = [];
  for (let i = pieces.length - 1; i >= 0; i--) {
    if (pieces[i].kind === 'mirror') inserts.unshift(pieces.splice(i, 1)[0]);
    else if (pieces[i].kind === 'slat') slats.unshift(pieces.splice(i, 1)[0]);
  }
  for (const p of pieces) p.color = p.accent ? zones[p.zone].design.accentColor : zones[p.zone].design.color;
  // light cove along the ceiling line and/or the floor line, stopping at doors and openings it would cross
  const cove = [];
  if (wall.cove === 'top' || wall.cove === 'both') cove.push(...subtractOpenings([[[0, H - 1.5], [W, H - 1.5]]], openings).map(sg => ({ at: 'top', seg: sg })));
  if (wall.cove === 'bottom' || wall.cove === 'both') cove.push(...subtractOpenings([[[0, 5], [W, 5]]], openings).map(sg => ({ at: 'bottom', seg: sg })));
  const pillarsOut = pillars.map(P => Object.assign({ finish: wall.pillars.finish, graze: !!wall.pillars.graze }, P));
  // headboard metal edge trim: up each outer edge and at each joint
  const trims = [];
  if (headboard && wall.headboard.trim && wall.headboard.trim !== 'none') {
    const ps = headboard.parts, xs = [ps[0].x0 - r / 2];
    for (let i = 0; i + 1 < ps.length; i++) xs.push((ps[i].x1 + ps[i + 1].x0) / 2);
    xs.push(ps[ps.length - 1].x1 + r / 2);
    xs.forEach(x => trims.push({ seg: [[x, ps[0].y0], [x, headboard.top]], finish: wall.headboard.trim }));
  }
  return { W, H, zones, pieces, inserts, slats, areas, sconces: sconces.concat(pillarSconces), cove, feature, pillars: pillarsOut, headboard: headboard && Object.assign({ bed: bedSize(wall.headboard.bed), base: !!wall.headboard.base }, headboard), trims, openings, warnings };
}

/**
 * Half-planes (a x + b y <= c) for a sloped panel field. slope = { bottom: [yLeft, yRight] | null, top: [...] | null }.
 * The edge gap is measured square to the stair line, as it would be on site.
 */
function sloped(slope, W, e) {
  const out = [];
  if (!slope) return out;
  if (slope.bottom && slope.bottom.length === 2 && isFinite(+slope.bottom[0]) && isFinite(+slope.bottom[1])) {
    const [yl, yr] = slope.bottom.map(Number), m = (yr - yl) / W, k = Math.sqrt(1 + m * m), g = e.floor * k;
    // y >= yl + m x + g   <=>   m x - y <= -(yl + g)
    out.push({ side: 'bottom', a: m, b: -1, c: -(yl + g), lo: Math.min(yl, yr) + g, line: [[0, yl], [W, yr]] });
  }
  if (slope.top && slope.top.length === 2 && isFinite(+slope.top[0]) && isFinite(+slope.top[1])) {
    const [yl, yr] = slope.top.map(Number), m = (yr - yl) / W, k = Math.sqrt(1 + m * m), g = e.ceiling * k;
    // y <= yl + m x - g   <=>   -m x + y <= yl - g
    out.push({ side: 'top', a: -m, b: 1, c: yl - g, hi: Math.max(yl, yr) - g, line: [[0, yl], [W, yr]] });
  }
  return out;
}
/** Keep the parts of segments where every half-plane holds. */
function clipSegs(segs, planes) {
  let out = segs;
  for (const P of planes) {
    const next = [];
    for (const sg of out) {
      const [a, b] = sg, fa = P.a * a[0] + P.b * a[1] - P.c, fb = P.a * b[0] + P.b * b[1] - P.c;
      if (fa <= 1e-9 && fb <= 1e-9) { next.push(sg); continue; }
      if (fa > 1e-9 && fb > 1e-9) continue;
      const t = fa / (fa - fb), m = lerp2(a, b, t);
      const ns = fa <= 1e-9 ? [a, m] : [m, b];
      if (Math.hypot(ns[1][0] - ns[0][0], ns[1][1] - ns[0][1]) > 0.05) next.push(sg.length > 2 ? ns.concat(sg.slice(2)) : ns);
    }
    out = next;
  }
  return out;
}

/**
 * Fibre-optic star points for zones whose design has stars (points per square foot of panel).
 * Seeded, so the same design always shows the same sky. Each point keeps an inch clear of the panel edge.
 */
function starSeed(k) { let h = 7; for (const ch of String(k || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h; }
function starPoints(L, seed) {
  const out = [];
  let s0 = (seed || 7) >>> 0;
  const rnd = () => { s0 = (s0 * 1664525 + 1013904223) >>> 0; return s0 / 4294967296; };
  L.pieces.forEach(p => {
    const d = L.zones[p.zone] && L.zones[p.zone].design;
    if (!d || !(d.stars > 0)) return;
    const want = d.stars * area(p.poly) / 144;
    let n = Math.floor(want) + (rnd() < want - Math.floor(want) ? 1 : 0);
    const b = bbox(p.poly);
    for (let tries = 0; n > 0 && tries < n * 30 + 30; tries++) {
      const q = [b.x0 + rnd() * b.w, b.y0 + rnd() * b.h];
      if (!pointInPolygon(q, p.poly)) continue;
      let m = Infinity;
      for (let i = 0; i < p.poly.length; i++) m = Math.min(m, pointSegDist(q, p.poly[i], p.poly[(i + 1) % p.poly.length]));
      if (m < 1) continue;
      if (p.holes.some(h => pointInPolygon(q, h))) continue;
      out.push({ x: q[0], y: q[1], zone: p.zone, tw: rnd() });
      n--;
    }
  });
  return out;
}

// ---------------------------------------------------------------- reveal lines (inlay strip and light channel)

/** Merge collinear, overlapping segments into the fewest segments. */
function mergeSegments(segs) {
  const groups = new Map();
  for (const [a, b] of segs) {
    let ux = b[0] - a[0], uy = b[1] - a[1];
    const L = Math.hypot(ux, uy);
    if (L < 1e-6) continue;
    ux /= L; uy /= L;
    if (ux < -1e-9 || (Math.abs(ux) <= 1e-9 && uy < 0)) { ux = -ux; uy = -uy; }
    const off = -uy * a[0] + ux * a[1];
    const key = Math.round(Math.atan2(uy, ux) * 1e5) + '|' + Math.round(off * 1e3);
    const t0 = ux * a[0] + uy * a[1], t1 = ux * b[0] + uy * b[1];
    if (!groups.has(key)) groups.set(key, { ux, uy, off, iv: [] });
    groups.get(key).iv.push([Math.min(t0, t1), Math.max(t0, t1)]);
  }
  const out = [];
  for (const g of groups.values()) {
    g.iv.sort((p, q) => p[0] - q[0]);
    let cur = null;
    const flush = () => { if (cur) out.push([[g.ux * cur[0] - g.uy * g.off, g.uy * cur[0] + g.ux * g.off], [g.ux * cur[1] - g.uy * g.off, g.uy * cur[1] + g.ux * g.off]]); };
    for (const iv of g.iv) {
      if (cur && iv[0] <= cur[1] + 1e-4) cur[1] = Math.max(cur[1], iv[1]);
      else { flush(); cur = iv.slice(); }
    }
    flush();
  }
  return out;
}
/** The part of segment ab inside rectangle X (Liang-Barsky), as [t0, t1] or null. */
function segInRect(a, b, X) {
  let t0 = 0, t1 = 1;
  const dx = b[0] - a[0], dy = b[1] - a[1];
  for (const [p, q] of [[-dx, a[0] - X.x0], [dx, X.x1 - a[0]], [-dy, a[1] - X.y0], [dy, X.y1 - a[1]]]) {
    if (Math.abs(p) < 1e-12) { if (q < 0) return null; continue; }
    const t = q / p;
    if (p < 0) { if (t > t1) return null; if (t > t0) t0 = t; } else { if (t < t0) return null; if (t < t1) t1 = t; }
  }
  return t1 - t0 > 1e-9 ? [t0, t1] : null;
}
const lerp2 = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
function subtractOpenings(segs, openings) {
  let out = segs;
  for (const O of openings) {
    const X = O.exp, next = [];
    for (const [a, b] of out) {
      const iv = segInRect(a, b, X);
      if (!iv) { next.push([a, b]); continue; }
      if (iv[0] > 1e-6) next.push([a, lerp2(a, b, iv[0])]);
      if (iv[1] < 1 - 1e-6) next.push([lerp2(a, b, iv[1]), b]);
    }
    out = next;
  }
  return out.filter(([a, b]) => Math.hypot(b[0] - a[0], b[1] - a[1]) > 0.05);
}
function isFeature(a, b, info) {
  if (typeof info.featureTest === 'function') return info.featureTest(a, b);
  const k = info.feature || 'all', dx = Math.abs(b[0] - a[0]), dy = Math.abs(b[1] - a[1]), L = Math.hypot(dx, dy) || 1;
  if (k === 'vertical') return dx / L < 1e-3;
  if (k === 'horizontal') return dy / L < 1e-3;
  if (k === 'nonaxis') return dx / L > 1e-3 && dy / L > 1e-3;
  if (k === 'cross') return (Math.abs(a[0] - info.cx) < 1e-3 && Math.abs(b[0] - info.cx) < 1e-3) || (Math.abs(a[1] - info.cy) < 1e-3 && Math.abs(b[1] - info.cy) < 1e-3);
  return true;
}
/**
 * The centre lines of the reveals between the pieces of one zone, from the cells before they were
 * moved apart. Each is [a, b, feature]. Lines on the zone's own edge, and through openings, are left out.
 */
function revealLines(cells, R, r, info, openings) {
  const Q = { x0: R.x0 - r / 2, y0: R.y0 - r / 2, x1: R.x1 + r / 2, y1: R.y1 + r / 2 }, segs = [];
  const onEdge = (a, b) => (Math.abs(a[0] - Q.x0) < 1e-6 && Math.abs(b[0] - Q.x0) < 1e-6) || (Math.abs(a[0] - Q.x1) < 1e-6 && Math.abs(b[0] - Q.x1) < 1e-6) ||
    (Math.abs(a[1] - Q.y0) < 1e-6 && Math.abs(b[1] - Q.y0) < 1e-6) || (Math.abs(a[1] - Q.y1) < 1e-6 && Math.abs(b[1] - Q.y1) < 1e-6);
  for (const c of cells) {
    const p = c.raw;
    if (!p) continue;
    for (let i = 0; i < p.length; i++) { const a = p[i], b = p[(i + 1) % p.length]; if (!onEdge(a, b)) segs.push([a, b]); }
  }
  const inR = [];
  for (const [a, b] of mergeSegments(segs)) {
    const iv = segInRect(a, b, R);
    if (iv) inR.push([lerp2(a, b, iv[0]), lerp2(a, b, iv[1])]);
  }
  return subtractOpenings(inR, openings).map(([a, b]) => [a, b, isFeature(a, b, info)]);
}
function segLen(s) { return Math.hypot(s[1][0] - s[0][0], s[1][1] - s[0][1]); }
/** The lines a zone's trim runs along: its feature lines (or every reveal if it has none, or if asked). */
function trimLines(zone) {
  const d = zone.design || {}, all = zone.lines || [];
  if (d.lineScope === 'all') return all;
  const f = all.filter(s => s[2]);
  return f.length ? f : all;
}
/** Inlay and light in plain numbers for one wall: inches of strip per finish, inches of light channel, mirror pieces. */
function trimTotals(L) {
  const t = { inlay: {}, led: 0, ledEdge: 0, ledLines: 0, ledCove: 0, mirrors: (L.inserts || []).length, surfaces: {},
    slats: (L.slats || []).length, slatLen: (L.slats || []).reduce((s, p) => s + bbox(p.poly).h, 0), sconces: (L.sconces || []).length,
    areas: (L.areas || []).reduce((o, a) => { o[a.type] = (o[a.type] || 0) + a.area; return o; }, {}) };
  for (const c of L.cove || []) { const v = segLen(c.seg); t.ledCove += v; t.led += v; }
  t.pillars = (L.pillars || []).length;
  for (const tr of L.trims || []) t.inlay[tr.finish] = (t.inlay[tr.finish] || 0) + segLen(tr.seg);
  t.ledGraze = (L.pillars || []).filter(p => p.graze).reduce((s, p) => s + (p.y1 - p.y0), 0); t.led += t.ledGraze;
  L.zones.forEach(z => {
    const d = z.design;
    if (!d || d.type === 'none') return;
    if (d.inlay && d.inlay !== 'none') t.inlay[d.inlay] = (t.inlay[d.inlay] || 0) + trimLines(z).reduce((s, x) => s + segLen(x), 0);
    if (d.led === 'edge' || d.led === 'both') { const v = (z.edgeLines || []).reduce((s, x) => s + segLen(x), 0); t.ledEdge += v; t.led += v; }
    if (d.led === 'lines' || d.led === 'both') { const v = trimLines(z).reduce((s, x) => s + segLen(x), 0); t.ledLines += v; t.led += v; }
    if (d.surface && d.surface !== 'plain') t.surfaces[d.surface] = true;
  });
  return t;
}

const api = {
  EPS, TINY_AREA, parseLength, fmtFrac, fmtIn, fmtFtIn,
  signedArea, area, ensureCCW, clean, rectPoly, bbox, centroid, clipHalfPlane, clipRect,
  convexIntersection, offsetConvex, minWidth, pointSegDist, polyDistance, isAxisRect, pointInConvex,
  rotatePoly, translatePoly,
  DESIGN_TYPES, OPENING_TYPES, openingName, defaultDesign, designCells, splitAround, computeWallLayout, countWord,
  isConvex, pointInPolygon, pointInAny, segIntersect, isSimple, untangle, offsetSimple, splitHalfPlane, clipRectAny,
  convexHull, pieceWidth, polyDistanceAny, arcPoints,
  TYPE_DEFAULTS, INLAY_TYPES, LED_TYPES, SURFACE_TYPES, LINE_SCOPES, MIRROR_TYPES, PATTERNS,
  SECTION_TYPES, CENTER_TYPES, WOODS, SCONCE_STYLES, METALS, SCONCE_PLACES, COVE_TYPES, LED_TEMPS, SCONCE_BOX,
  defaultComp, defaultSconces, composeRects, sconcePositions, isBoxOpening,
  PILLAR_FINISHES, defaultPillars, pillarRects, defaultLevels, tierSpans, tierRunsOnWall,
  BED_SIZES, WING_TYPES, defaultHeadboard, bedSize, headboardRects,
  mergeSegments, revealLines, trimLines, trimTotals, segLen, sloped, clipSegs, starPoints, starSeed,
  CUSTOM_TILES, CUSTOM_LIMITS, defaultCustom, sanitizeCustom, customRing, validateCustom,
};
if (typeof module !== 'undefined' && module.exports) module.exports = api;
else root.RRGeom = api;
})(typeof window !== 'undefined' ? window : globalThis);

